import { BackgroundConfig } from "../background-config";
import { ViewerSelectionState } from "./viewer-selection-state";
import { ViewerStateBindingController } from "./viewer-state-binding-controller";
import { ViewerVisualizationRuntime } from "./viewer-visualization-runtime";
import { ViewerShaderSourceController, makeXOpatSourceToken } from "./viewer-shader-source-controller";
import { assembleBackgroundShaders, assembleVisualizationShaders } from "./assemble-render-output";
import { buildShaderIdNamespace, renameShaderIds } from "../visualization/shader-id-namespace";
import { readPixelScale, computeOverlayWidth } from "./overlay-pixel-scale";
import { canCompositeRegions } from "../virtual-region-protocol";

export interface OpenViewerWithOptions {
    dataMode?: "replace" | "merge" | "merge-exact";
    backgroundMode?: "replace" | "merge" | "merge-exact";
    historyMode?: "auto" | "skip" | "visualization-step" | "content-switch" | "reset-history";
    fromHistory?: boolean;
    preserveHistoryOnBackgroundChange?: boolean;
    warnOnHistoryBoundary?: boolean;
    historyLabel?: string;
    strictVisualization?: boolean;
    skipVisualizationCapabilityCheck?: boolean;
    suppressDialogsOnVisualizationFailure?: boolean;
    /**
     * Force a full content reopen of every viewer even when the selection /
     * render fingerprint is unchanged. Needed when the RENDER changes without the
     * `activeBackgroundIndex` changing — e.g. switching a parent's
     * `virtualizationMode` (none ⇄ overlaid both select the parent index).
     */
    force?: boolean;
}

export interface ViewerOpenPipelineDependencies {
    appContext: ApplicationContext;
    env: XOpatCoreConfig;
    viewerManager: any;
    getConfig: () => any;
    cloneRuntimeState: <T>(value: T) => T;
    safeStringify: (value: any) => string;
    runLoaderOnce: () => Promise<void> | void;
    visualizationRuntime: ViewerVisualizationRuntime;
    stateBindings: ViewerStateBindingController;
}

export class ViewerOpenPipeline {
    constructor(private readonly deps: ViewerOpenPipelineDependencies) {}

    private async waitForViewerRenderReady(viewer: OpenSeadragon.Viewer, attempts = 8) {
        const drawer: any = viewer?.drawer;
        const hasSizedCanvas = () => {
            const canvas = drawer?.canvas;
            return !!(canvas && canvas.width > 0 && canvas.height > 0);
        };

        if (hasSizedCanvas()) {
            return;
        }

        for (let attempt = 0; attempt < attempts; attempt++) {
            try {
                viewer.forceResize?.();
            } catch (_) {
                // ignore and retry on next frame
            }

            await new Promise<void>((resolve) => {
                OpenSeadragon.requestAnimationFrame(() => resolve());
            });

            if (hasSizedCanvas()) {
                return;
            }
        }
    }

    /**
     * The navigator mirrors main.world.add-item by calling its own addTiledImage asynchronously
     * (one Promise hop per tile). For the last tile in a batch there's no further await in the
     * caller, so control returns to the render pipeline before the navigator's world has caught
     * up. If resumeRendering then fires the deferred rebuild with a stale navigator world count,
     * setDimensions allocates stencil/atlas textures smaller than the shader configs expect and
     * every subsequent draw errors with "Attachment layer is greater than texture layer count"
     * until the navigator eventually catches up and triggers a second rebuild.
     *
     * Poll until navigator.world matches main.world, so both drawers' rebuilds see the same
     * world state. Rendering is suspended during this wait, so no frames are drawn while we poll.
     */
    private async waitForNavigatorParity(viewer: OpenSeadragon.Viewer, attempts = 30) {
        const navigator: any = viewer?.navigator;
        if (!navigator?.world) return;
        const targetCount = viewer.world?.getItemCount?.() ?? 0;
        if (targetCount === 0) return;

        for (let attempt = 0; attempt < attempts; attempt++) {
            if ((navigator.world.getItemCount?.() ?? 0) >= targetCount) {
                return;
            }
            await new Promise<void>((resolve) => {
                OpenSeadragon.requestAnimationFrame(() => resolve());
            });
        }

        const finalCount = navigator.world.getItemCount?.() ?? 0;
        if (finalCount < targetCount) {
            console.warn(
                "Navigator world did not catch up with main viewer before render config.",
                { mainCount: targetCount, navCount: finalCount }
            );
        }
    }

    async updateViewerSelection(
        viewerIndex: number,
        selection: {
            backgroundIndex?: number | null;
            visualizationIndex?: number | null;
        },
        opts: OpenViewerWithOptions = {}
    ) {
        const appContext = this.deps.appContext;
        const activeBackground = ViewerSelectionState.normalizeSelectionValue(
            appContext.getOption("activeBackgroundIndex", undefined, true, true)
        ) || [];

        while (activeBackground.length <= viewerIndex) {
            activeBackground.push(activeBackground[0]);
        }

        if (selection.backgroundIndex !== undefined) {
            activeBackground[viewerIndex] = Number.isInteger(selection.backgroundIndex)
                ? selection.backgroundIndex as number
                : undefined;
        }

        // Slot-aligned vizSpec: only the targeted slot carries a value;
        // openViewerWith's fold leaves `undefined` slots untouched (so other
        // viewers' viz selections are preserved) and writes the new value
        // onto the slot's background entry AFTER previousSnapshot capture —
        // mutating live config here would clobber the diff and short-circuit
        // the rebuild as historyMode="skip".
        let vizSpec: Array<number | undefined | null> | undefined = undefined;
        if (selection.visualizationIndex !== undefined) {
            vizSpec = new Array(activeBackground.length).fill(undefined);
            vizSpec[viewerIndex] = Number.isInteger(selection.visualizationIndex)
                ? selection.visualizationIndex as number
                : null;
        }

        return this.openViewerWith(
            undefined,
            undefined,
            undefined,
            activeBackground,
            vizSpec as any,
            opts
        );
    }

    /**
     * Switch the render mode of a virtualized parent background between
     * `none` / `sidebyside` / `overlaid`. Runtime-switchable: the decomposition
     * is stored once on the parent; this only recomputes the selection and
     * reopens — it never re-detects regions.
     *
     *  - `none`       → one viewer shows the parent (uncropped).
     *  - `sidebyside` → each region's child background fills its own viewer slot.
     *                   Children are SEPARATE backgrounds with their own ids, so
     *                   IO/annotations key per-region (not by the parent) — see the
     *                   warning + docs (VIRTUAL_VIEWPORTS_SPLIT.md).
     *  - `overlaid`   → all region chunks co-resident in ONE viewer that keeps the
     *                   PARENT's identity (IO/annotations behave like the un-split
     *                   slide). Alignment (placement via each region's `transform`)
     *                   is a later phase; for now the chunks overlay at origin.
     */
    async setVirtualizationMode(
        parentBgId: string,
        mode: VirtualizationMode,
        opts: OpenViewerWithOptions = {}
    ) {
        const config = this.deps.getConfig();
        // Make sure the children exist before we map regions → indices.
        BackgroundConfig.expandVirtualBackgrounds(config);
        const backgrounds: any[] = Array.isArray(config.background) ? config.background : [];

        const parentIndex = backgrounds.findIndex((b) => b && b.id === parentBgId);
        if (parentIndex < 0) {
            console.warn(`[virtualization] setVirtualizationMode: no background with id "${parentBgId}".`);
            return false;
        }
        const parent = backgrounds[parentIndex];
        const decomp: VirtualDecomposition | undefined = parent.virtualization;
        if (!decomp || !Array.isArray(decomp.regions) || decomp.regions.length < 1) {
            console.warn(`[virtualization] background "${parentBgId}" has no stored decomposition to switch.`);
            return false;
        }

        // A split recomposites the tiles on each region BORDER as plain images
        // (see `canCompositeRegions`), and it does so for EVERY data layer of the
        // stack, not just the background. Refuse here, while the un-split parent
        // is still on screen, rather than reopening into a viewer ringed with
        // failed tiles. Only reachable when the parent is already open; a session
        // authored straight into a split is caught by the source-level gate.
        if (mode !== "none") {
            const world: any = this.deps.viewerManager.getViewer?.(parentBgId)?.world;
            if (world?.getItemCount) {
                for (let i = 0; i < world.getItemCount(); i++) {
                    const source = world.getItemAt(i)?.source;
                    if (source && !canCompositeRegions(source)) {
                        console.warn(`[virtualization] background "${parentBgId}" cannot be split: a data layer's tiles are not plain images.`);
                        try {
                            Dialogs.show($.t("virtualization.sourceNotCompositable"), 12000, Dialogs.MSG_WARN);
                        } catch (_) { /* Dialogs not ready */ }
                        return false;
                    }
                }
            }
        }

        const childIndices: number[] = [];
        for (const region of decomp.regions) {
            const childId = UTILITIES.sanitizeID(`${parentBgId}::${region.id}`);
            const idx = backgrounds.findIndex((b) => b && b.id === childId);
            if (idx >= 0) childIndices.push(idx);
        }

        let activeBg: number[];
        if (mode === "sidebyside") {
            if (childIndices.length < 1) {
                console.warn(`[virtualization] no expanded children for "${parentBgId}".`);
                return false;
            }
            parent.virtualizationMode = "sidebyside";
            activeBg = childIndices;
            // Side-by-side opens each region as a SEPARATE background/viewer with
            // its own id, so annotations/IO key per-region (not by the parent) and
            // would need coordinate offsets to share state. Warn the user; the
            // clean alternative is "overlaid" (single viewer, parent identity).
            try {
                Dialogs.show($.t("virtualization.sidebysideWarning"), 12000, Dialogs.MSG_WARN);
            } catch (_) { /* Dialogs not ready */ }
        } else if (mode === "overlaid") {
            // Overlaid keeps the PARENT selected (identity = parent → IO/annotations
            // behave like the un-split slide); the open pipeline detects the parent's
            // overlaid mode and renders all children's cropped stacks into this one
            // viewer's world.
            parent.virtualizationMode = "overlaid";
            activeBg = [parentIndex];
        } else {
            parent.virtualizationMode = "none";
            activeBg = [parentIndex];
        }

        // Seed the cropped-source geometry cache from the parent slide that is
        // currently open (under the previous mode), so the initial cropped open
        // is synchronously-ready and avoids the async render race that otherwise
        // produces transient square / blank / WebGL-framebuffer states.
        if (mode !== "none") {
            try {
                const OSDref: any = (window as any).OpenSeadragon;
                const seed = OSDref?.CroppedTileSource?.seedParentGeometry;
                const pViewer: any = this.deps.viewerManager.getViewer?.(parentBgId);
                const world: any = pViewer?.world;
                if (seed && world?.getItemCount) {
                    // Seed every loaded data source by its DataID (the cropped
                    // sources key on the same id). The pipeline stamps each item
                    // with `__xopatLoadKey = "data:<dataId>"`. Also PERSIST the
                    // geometry into params so a later direct reload into the split
                    // is synchronously-ready (no async first-pass square race).
                    const cfg = this.deps.appContext._dangerouslyAccessConfig();
                    cfg.params = cfg.params || {};
                    const dimsMap = cfg.params.virtualSourceDims = cfg.params.virtualSourceDims || {};
                    for (let i = 0; i < world.getItemCount(); i++) {
                        const item = world.getItemAt(i);
                        const key = item?.__xopatLoadKey;
                        if (item?.source && typeof key === "string" && key.startsWith("data:")) {
                            const dataId = key.slice(5);
                            const geom = seed(dataId, item.source);
                            if (geom) dimsMap[dataId] = geom;
                        }
                    }
                }
            } catch (_) { /* best-effort seeding */ }
        }

        return this.openViewerWith(
            undefined,
            undefined,
            undefined,
            activeBg,
            undefined,
            // Force a reopen: switching mode can change the RENDER without changing
            // `activeBackgroundIndex` (none ⇄ overlaid both select the parent), so
            // the pipeline's diff would otherwise treat it as a no-op.
            { historyMode: "content-switch", force: true, ...opts }
        );
    }

    async openViewerWith(
        data = undefined,
        background: BackgroundItem[] | undefined = undefined,
        visualizations: VisualizationItem[] | undefined = undefined,
        bgSpec: number | Array<number | undefined> | undefined | null = undefined,
        vizSpec: number | Array<number | undefined> | undefined | null = undefined,
        opts: OpenViewerWithOptions = {}
    ) {
        const { appContext, env, viewerManager, cloneRuntimeState, safeStringify, visualizationRuntime, stateBindings } = this.deps;

        if (appContext.getOption("webglDebugMode")) {
            console.log("[pipeline] openViewerWith ENTER", {
                hasData: data !== undefined,
                hasBackground: background !== undefined,
                hasVisualizations: visualizations !== undefined,
                bgSpec,
                vizSpec,
                opts,
                stack: new Error("openViewerWith called from").stack?.split("\n").slice(1, 6).join("\n    "),
            });
        }

        const normalizeHistorySelection = (value: any): Array<number | undefined> | undefined => {
            if (value == null) return undefined;
            if (Array.isArray(value)) {
                return value.map((entry: any) => Number.isInteger(entry) ? entry : undefined);
            }
            return Number.isInteger(value) ? [value] : undefined;
        };

        const cloneForHistory = <T>(value: T): T => {
            if (value === undefined || value === null) return value;
            try {
                return JSON.parse(safeStringify(value));
            } catch (e) {
                console.warn("Failed to snapshot state for history.", e);
                return value;
            }
        };

        const normalizeCollectionMode = (value: any): "replace" | "merge" | "merge-exact" =>
            value === "merge" ? "merge"
                : value === "merge-exact" ? "merge-exact"
                    : "replace";
        const cloneForMerge = <T>(value: T): T => cloneRuntimeState(value);
        const sameMergedDataEntry = (a: any, b: any) => safeStringify(a) === safeStringify(b);
        const remapSelectionByIndexMap = (
            value: number | Array<number | undefined> | undefined | null,
            indexMap: Map<number, number>
        ): number | Array<number | undefined> | undefined | null => {
            if (value == null) return value;
            if (Array.isArray(value)) {
                return value.map((entry: any) => Number.isInteger(entry) ? indexMap.get(entry) : entry);
            }
            return Number.isInteger(value) ? indexMap.get(value) : value;
        };
        const remapBackgroundDataReferences = (
            backgroundItems: Array<BackgroundItem | BackgroundConfig> | undefined,
            dataIndexMap: Map<number, number>
        ) => {
            if (!Array.isArray(backgroundItems) || dataIndexMap.size < 1) return;

            const remapNestedLayer = (layer: any) => {
                if (!layer || typeof layer !== "object") return;

                if (Array.isArray(layer.dataReferences)) {
                    layer.dataReferences = layer.dataReferences.map((ref: any) =>
                        Number.isInteger(ref) ? (dataIndexMap.get(ref) ?? ref) : ref
                    );
                }

                if (layer.shaders && typeof layer.shaders === "object" && !Array.isArray(layer.shaders)) {
                    Object.values(layer.shaders).forEach(remapNestedLayer);
                }
            };

            backgroundItems.forEach((item: any) => {
                if (!item || typeof item !== "object") return;

                if (Number.isInteger(item.dataReference)) {
                    item.dataReference = dataIndexMap.get(item.dataReference) ?? item.dataReference;
                }

                if (Array.isArray(item.shaders)) {
                    item.shaders.forEach(remapNestedLayer);
                }
            });
        };
        const remapVisualizationDataReferences = (
            visualizationItems: VisualizationItem[] | undefined,
            dataIndexMap: Map<number, number>
        ) => {
            if (!Array.isArray(visualizationItems) || dataIndexMap.size < 1) return;

            visualizationItems.forEach((visualization: any) => {
                if (!visualization?.shaders || typeof visualization.shaders !== "object") return;

                forEachVisualizationShader(visualization.shaders as Record<string, any>, (shader) => {
                    if (!Array.isArray(shader?.dataReferences)) return;
                    shader.dataReferences = shader.dataReferences.map((ref: any) =>
                        Number.isInteger(ref) ? (dataIndexMap.get(ref) ?? ref) : ref
                    );
                });
            });
        };
        const mergeDataCollection = (
            baseData: DataID[],
            incomingData: DataID[],
            exact = false
        ): { data: DataID[]; indexMap: Map<number, number>; baseToNew?: Map<number, number>; } => {
            // "exact" mode treats incomingData as the complete intended set:
            // existing entries that have no identity match in incoming are
            // dropped. Identity-matching incoming entries reuse the existing
            // slot's data so cross-references stay stable. Returns an
            // additional `baseToNew` map so callers can remap any preserved
            // references (existing visualizations / backgrounds keyed by base
            // data index) through the new data layout.
            if (exact) {
                const base = Array.isArray(baseData) ? baseData : [];
                const incoming = Array.isArray(incomingData) ? incomingData : [];
                const data: DataID[] = [];
                const indexMap = new Map<number, number>();
                const baseToNew = new Map<number, number>();
                incoming.forEach((entry: any, index: number) => {
                    const existingIndex = base.findIndex((candidate: any) => sameMergedDataEntry(candidate, entry));
                    if (existingIndex >= 0) {
                        data.push(cloneForMerge(base[existingIndex]));
                        baseToNew.set(existingIndex, data.length - 1);
                    } else {
                        data.push(cloneForMerge(entry));
                    }
                    indexMap.set(index, data.length - 1);
                });
                return { data, indexMap, baseToNew };
            }

            const mergedData = cloneForMerge(Array.isArray(baseData) ? baseData : []);
            const indexMap = new Map<number, number>();

            (Array.isArray(incomingData) ? incomingData : []).forEach((entry: any, index: number) => {
                const existingIndex = mergedData.findIndex((candidate: any) => sameMergedDataEntry(candidate, entry));
                if (existingIndex >= 0) {
                    indexMap.set(index, existingIndex);
                    return;
                }

                mergedData.push(cloneForMerge(entry));
                indexMap.set(index, mergedData.length - 1);
            });

            return { data: mergedData, indexMap };
        };
        const mergeBackgroundCollection = (
            baseBackground: Array<BackgroundItem | BackgroundConfig>,
            incomingBackground: Array<BackgroundItem | BackgroundConfig>,
            exact = false
        ): { background: Array<BackgroundItem | BackgroundConfig>; indexMap: Map<number, number>; } => {
            const findExistingIndex = (
                pool: Array<BackgroundItem | BackgroundConfig>,
                clonedEntry: any
            ): number => {
                let idx = pool.findIndex((candidate: any) =>
                    candidate?.id && clonedEntry?.id && candidate.id === clonedEntry.id
                );
                if (idx < 0) {
                    idx = pool.findIndex((candidate: any) =>
                        appContext.sameBackground(candidate, clonedEntry)
                    );
                }
                return idx;
            };

            // "exact" mode rebuilds the list to exactly mirror incoming: any
            // base entries without a counterpart in incoming are dropped,
            // matched entries reuse the freshly-cloned incoming payload.
            if (exact) {
                const base = Array.isArray(baseBackground) ? baseBackground : [];
                const incoming = Array.isArray(incomingBackground) ? incomingBackground : [];
                const background: Array<BackgroundItem | BackgroundConfig> = [];
                const indexMap = new Map<number, number>();
                incoming.forEach((entry: any, index: number) => {
                    const clonedEntry = cloneForMerge(entry);
                    background.push(clonedEntry);
                    indexMap.set(index, background.length - 1);
                });
                return { background, indexMap };
            }

            const mergedBackground = cloneForMerge(Array.isArray(baseBackground) ? baseBackground : []);
            const indexMap = new Map<number, number>();

            (Array.isArray(incomingBackground) ? incomingBackground : []).forEach((entry: any, index: number) => {
                const clonedEntry = cloneForMerge(entry);
                let mergedIndex = findExistingIndex(mergedBackground, clonedEntry);

                if (mergedIndex >= 0) {
                    mergedBackground[mergedIndex] = clonedEntry;
                } else {
                    mergedBackground.push(clonedEntry);
                    mergedIndex = mergedBackground.length - 1;
                }

                indexMap.set(index, mergedIndex);
            });

            return { background: mergedBackground, indexMap };
        };

        const captureLoadSnapshotFromConfig = (source: any = appContext._dangerouslyAccessConfig()) => ({
            data: Array.isArray(source?.data) ? cloneForHistory(source.data) : [],
            background: Array.isArray(source?.background) ? cloneForHistory(source.background) : [],
            visualizations: Array.isArray(source?.visualizations) ? cloneForHistory(source.visualizations) : [],
            activeBackgroundIndex: normalizeHistorySelection(
                appContext.getOption("activeBackgroundIndex", undefined, true, true)
            ),
        });

        // Viz index for slot `viewerIndex` is derived from the slot's bg entry
        // in the snapshot: snapshot.background[activeBg[k]].visualizationIndex.
        // Falls back to slot 0's bg if the slot is out of range (legacy
        // broadcast behavior).
        const vizIndexFromSnapshot = (snapshot: any, viewerIndex: number): number | undefined => {
            const backgrounds = Array.isArray(snapshot?.background) ? snapshot.background : [];
            const activeBg = normalizeHistorySelection(snapshot?.activeBackgroundIndex) || [];
            const bgIdx = viewerIndex < activeBg.length ? activeBg[viewerIndex] : activeBg[0];
            if (!Number.isInteger(bgIdx)) return undefined;
            const bg: any = backgrounds[bgIdx as number];
            const v = bg?.visualizationIndex;
            return Number.isInteger(v) ? v as number : undefined;
        };

        const selectedBackgroundIdsFromSnapshot = (snapshot: any): string[] => {
            const selected = normalizeHistorySelection(snapshot?.activeBackgroundIndex) || [];
            const backgrounds = Array.isArray(snapshot?.background) ? snapshot.background : [];
            return selected
                .map((index: number | undefined) => Number.isInteger(index) ? backgrounds[index as number] : undefined)
                .filter(Boolean)
                .map((entry: BackgroundItem | BackgroundConfig) => appContext.registerConfig(entry).id);
        };

        const selectedVisualizationConfigsFromSnapshot = (snapshot: any) => {
            const activeBg = normalizeHistorySelection(snapshot?.activeBackgroundIndex) || [];
            const visualizationItems = Array.isArray(snapshot?.visualizations) ? snapshot.visualizations : [];
            return activeBg.map((_: any, slot: number) => {
                const v = vizIndexFromSnapshot(snapshot, slot);
                return Number.isInteger(v) ? visualizationItems[v as number] : undefined;
            });
        };

        const buildVisibleLoadFingerprint = (snapshot: any) => safeStringify({
            selectedBackgroundIds: selectedBackgroundIdsFromSnapshot(snapshot),
            selectedVisualizations: selectedVisualizationConfigsFromSnapshot(snapshot),
            data: snapshot?.data || [],
        });

        const loadSnapshotsEqual = (a: any, b: any) => safeStringify(a) === safeStringify(b);
        const visibleLoadChanged = (a: any, b: any) => buildVisibleLoadFingerprint(a) !== buildVisibleLoadFingerprint(b);
        const selectionAt = (value: any, viewerIndex: number): number | undefined => {
            const selected = normalizeHistorySelection(value) || [];
            if (viewerIndex < selected.length) {
                return selected[viewerIndex];
            }
            return selected[0];
        };
        const dataSpecFromRef = (dataSet: any[], ref: any) => typeof ref === "number" ? dataSet?.[ref] : ref;
        const dataIdFromSpec = (spec: any) => {
            if (spec == null) return undefined;
            return spec && typeof spec === "object" && spec.dataID ? spec.dataID : spec;
        };
        const forEachVisualizationShader = (
            shaderMap: Record<string, any> | undefined,
            callback: (shader: any, shaderId: string, path: string[]) => void,
            path: string[] = []
        ) => {
            if (!shaderMap || typeof shaderMap !== "object") {
                return;
            }
            for (const [shaderId, shader] of Object.entries(shaderMap)) {
                if (!shader || typeof shader !== "object") {
                    continue;
                }
                const nextPath = path.concat([shaderId]);
                callback(shader, shaderId, nextPath);
                if (shader.shaders && typeof shader.shaders === "object" && !Array.isArray(shader.shaders)) {
                    forEachVisualizationShader(shader.shaders, callback, nextPath);
                }
            }
        };
        const forEachNestedShaderLayer = (
            layer: any,
            callback: (shader: any, path: string[]) => void,
            path: string[] = []
        ) => {
            if (!layer || typeof layer !== "object") {
                return;
            }
            callback(layer, path);
            if (layer.shaders && typeof layer.shaders === "object" && !Array.isArray(layer.shaders)) {
                for (const [childId, child] of Object.entries(layer.shaders)) {
                    forEachNestedShaderLayer(child, callback, path.concat([childId]));
                }
            }
        };
        const collectViewerReferencedData = (snapshot: any, viewerIndex: number) => {
            const backgrounds = Array.isArray(snapshot?.background) ? snapshot.background : [];
            const visualizationItems = Array.isArray(snapshot?.visualizations) ? snapshot.visualizations : [];
            const dataSet = Array.isArray(snapshot?.data) ? snapshot.data : [];
            const bgIndex = selectionAt(snapshot?.activeBackgroundIndex, viewerIndex);
            const vizIndex = vizIndexFromSnapshot(snapshot, viewerIndex);
            const refs = new Set<any>();
            const bgEntry = Number.isInteger(bgIndex) ? backgrounds[bgIndex as number] : undefined;
            const vizEntry = Number.isInteger(vizIndex) ? visualizationItems[vizIndex as number] : undefined;

            if (bgEntry?.dataReference !== undefined) {
                refs.add(dataIdFromSpec(dataSpecFromRef(dataSet, bgEntry.dataReference)));
            }
            if (Array.isArray(bgEntry?.shaders)) {
                for (const shader of bgEntry.shaders) {
                    forEachNestedShaderLayer(shader, (entry) => {
                        for (const ref of entry?.dataReferences || []) {
                            refs.add(dataIdFromSpec(dataSpecFromRef(dataSet, ref)));
                        }
                    });
                }
            }
            if (vizEntry?.shaders) {
                forEachVisualizationShader(vizEntry.shaders as Record<string, any>, (shader) => {
                    for (const ref of shader?.dataReferences || []) {
                        refs.add(dataIdFromSpec(dataSpecFromRef(dataSet, ref)));
                    }
                });
            }
            return Array.from(refs);
        };
        const buildViewerNatureFingerprint = (snapshot: any, viewerIndex: number) => {
            const backgrounds = Array.isArray(snapshot?.background) ? snapshot.background : [];
            const visualizationItems = Array.isArray(snapshot?.visualizations) ? snapshot.visualizations : [];
            const dataSet = Array.isArray(snapshot?.data) ? snapshot.data : [];
            const bgIndex = selectionAt(snapshot?.activeBackgroundIndex, viewerIndex);
            const vizIndex = vizIndexFromSnapshot(snapshot, viewerIndex);
            const bgEntry = Number.isInteger(bgIndex) ? backgrounds[bgIndex as number] : undefined;
            const vizEntry = Number.isInteger(vizIndex) ? visualizationItems[vizIndex as number] : undefined;

            return safeStringify({
                viewerIndex,
                background: bgEntry ? {
                    id: bgEntry.id,
                    protocol: bgEntry.protocol,
                    dataReference: dataIdFromSpec(dataSpecFromRef(dataSet, bgEntry.dataReference)),
                    shaders: Array.isArray(bgEntry.shaders) ? bgEntry.shaders.flatMap((shader: any, index: number) => {
                        const layers: Array<{ id: any; type: any; dataReferences: any[]; path: string[]; }> = [];
                        forEachNestedShaderLayer(shader, (entry, path) => {
                            layers.push({
                                id: entry?.id,
                                type: entry?.type,
                                dataReferences: Array.isArray(entry?.dataReferences)
                                    ? entry.dataReferences.map((ref: number) => dataIdFromSpec(dataSpecFromRef(dataSet, ref)))
                                    : [],
                                path: path.length ? path : [String(index)],
                            });
                        }, [String(index)]);
                        return layers;
                    }) : [],
                } : null,
                visualization: vizEntry ? {
                    protocol: vizEntry.protocol,
                    shaders: (() => {
                        const layers: Array<{ id: string; type: any; dataReferences: any[]; path: string[]; }> = [];
                        forEachVisualizationShader(vizEntry.shaders || {}, (shader, shaderId, path) => {
                            layers.push({
                                id: shader?.id || shaderId,
                                type: shader?.type,
                                dataReferences: Array.isArray(shader?.dataReferences)
                                    ? shader.dataReferences.map((ref: number) => dataIdFromSpec(dataSpecFromRef(dataSet, ref)))
                                    : [],
                                path,
                            });
                        });
                        return layers;
                    })(),
                } : null,
            });
        };
        const buildViewerRenderFingerprint = (snapshot: any, viewerIndex: number) => {
            const backgrounds = Array.isArray(snapshot?.background) ? snapshot.background : [];
            const visualizationItems = Array.isArray(snapshot?.visualizations) ? snapshot.visualizations : [];
            const dataSet = Array.isArray(snapshot?.data) ? snapshot.data : [];
            const bgIndex = selectionAt(snapshot?.activeBackgroundIndex, viewerIndex);
            const vizIndex = vizIndexFromSnapshot(snapshot, viewerIndex);

            return safeStringify({
                viewerIndex,
                background: Number.isInteger(bgIndex) ? backgrounds[bgIndex as number] : null,
                visualization: Number.isInteger(vizIndex) ? visualizationItems[vizIndex as number] : null,
                data: collectViewerReferencedData(snapshot, viewerIndex),
            });
        };

        const restoreLoadSnapshot = async (snapshot: any) => {
            if (!snapshot) return false;
            // Per-viewer viz lives on the cloned `background` array (each entry
            // carries its `visualizationIndex`); no separate vizSpec needed.
            return await this.openViewerWith(
                cloneForHistory(snapshot.data),
                cloneForHistory(snapshot.background),
                cloneForHistory(snapshot.visualizations),
                cloneForHistory(snapshot.activeBackgroundIndex),
                undefined,
                {
                    historyMode: "skip",
                    fromHistory: true,
                    warnOnHistoryBoundary: false,
                }
            );
        };

        const parseLooseBoolean = (value: any, fallback = false) => {
            if (value === undefined || value === null) return fallback;
            if (value === false || value === "false") return false;
            if (value === true || value === "true") return true;
            return Boolean(value);
        };

        const config = this.deps.getConfig();
        const previousSnapshot = captureLoadSnapshotFromConfig(config);
        const history = appContext.history;
        const hadOpenViewerState = (viewerManager.viewers || []).some((viewer: OpenSeadragon.Viewer | undefined) => {
            if (!viewer) return false;
            if (typeof viewer.isOpen === "function" && viewer.isOpen()) return true;
            return (viewer.world?.getItemCount?.() || 0) > 0;
        });
        const existingBackground = Array.isArray(config.background) ? config.background : [];
        const existingVisualizations = Array.isArray(config.visualizations) ? config.visualizations : [];
        const existingData = Array.isArray(config.data) ? config.data : [];
        const dataMode = normalizeCollectionMode(opts.dataMode);
        const backgroundMode = normalizeCollectionMode(opts.backgroundMode);
        let normalizedBackground = background === null ? [] : cloneForMerge(background);
        let normalizedVisualizations = visualizations === null ? [] : cloneForMerge(visualizations);
        let normalizedData = data === null ? [] : cloneForMerge(data);
        let effectiveBgSpec = bgSpec;

        if (data !== null && (dataMode === "merge" || dataMode === "merge-exact") && Array.isArray(normalizedData)) {
            const mergedData = mergeDataCollection(existingData, normalizedData, dataMode === "merge-exact");
            remapBackgroundDataReferences(normalizedBackground as Array<BackgroundItem | BackgroundConfig>, mergedData.indexMap);
            remapVisualizationDataReferences(normalizedVisualizations as VisualizationItem[], mergedData.indexMap);
            // merge-exact may have shifted indices of preserved entries: any
            // existing visualizations / backgrounds we keep beyond this point
            // still reference data by BASE index, so remap them through the
            // base→new map. Entries pointing to dropped data become a numeric
            // index outside `mergedData.data.length` — validators downstream
            // (validateVisualizationCollection) will surface those as errors.
            if (mergedData.baseToNew) {
                remapBackgroundDataReferences(existingBackground as Array<BackgroundItem | BackgroundConfig>, mergedData.baseToNew);
                remapVisualizationDataReferences(existingVisualizations as VisualizationItem[], mergedData.baseToNew);
            }
            normalizedData = mergedData.data;
        }

        if (background !== null && (backgroundMode === "merge" || backgroundMode === "merge-exact") && Array.isArray(normalizedBackground)) {
            const mergedBackground = mergeBackgroundCollection(existingBackground, normalizedBackground, backgroundMode === "merge-exact");
            normalizedBackground = mergedBackground.background as any;
            effectiveBgSpec = remapSelectionByIndexMap(bgSpec, mergedBackground.indexMap);
        }

        if (typeof normalizedData !== "undefined") config.data = normalizedData;
        else if (!Array.isArray(config.data)) config.data = existingData;
        if (typeof normalizedBackground !== "undefined") config.background = normalizedBackground;
        else if (!Array.isArray(config.background)) config.background = existingBackground;
        if (typeof normalizedVisualizations !== "undefined") config.visualizations = normalizedVisualizations;
        else if (!Array.isArray(config.visualizations)) config.visualizations = existingVisualizations;

        if (!Array.isArray(config.data)) config.data = [];
        if (!Array.isArray(config.background)) config.background = [];
        if (!Array.isArray(config.visualizations)) config.visualizations = [];

        const strictVisualization = !!opts.strictVisualization;
        const visualizationValidation = visualizationRuntime.validateVisualizationCollection(config.visualizations as any, config.data as any);
        if (visualizationValidation.issues.length > 0) {
            console.warn("Visualization validation issues detected.", visualizationValidation.issues);
            if (strictVisualization) {
                USER_INTERFACE.Loading.show(false);
                throw new Error("Visualization validation failed: " + visualizationValidation.issues.join(" | "));
            }
            // Non-strict: the offending layers have already been dropped from the
            // sanitized collection, and until now that happened in silence — the
            // viewer opened with fewer overlays than the session asked for and
            // nothing said so. One aggregated message; the individual issue
            // strings stay in the console above, since they name internal shader
            // ids and are not translatable.
            visualizationRuntime.warnVisualization($.t("error.visualizationValidationIssues"));
        }
        // Renderer-side verdict on the layers that survived. Report-only: these layers
        // still render (the renderer substitutes or ignores what it cannot use), but
        // until now a config whose control types or params were wrong reached the
        // renderer in silence and the user only found out when the result looked odd —
        // or, in the worst case, when the resulting program failed to compile. The
        // individual lines name shader ids and JSON Pointer paths, so they go to the
        // log, not the toast.
        if (visualizationValidation.advisories.length > 0) {
            APPLICATION_CONTEXT.log("app.visualization").warn(
                `Visualization config rejected by the renderer's own validation (${visualizationValidation.advisories.length} finding(s)):\n`
                + visualizationValidation.advisories.join("\n")
            );
            visualizationRuntime.warnVisualization($.t("error.visualizationParamsIgnored"));
        }
        config.visualizations = visualizationValidation.visualizations as any;

        const renderingCapability = opts.skipVisualizationCapabilityCheck
            ? ((appContext as any).__renderingCapability || { ok: true })
            : visualizationRuntime.getRenderingCapability(false);
        if (strictVisualization && Array.isArray(config.visualizations) && config.visualizations.length > 0 && !renderingCapability.ok) {
            USER_INTERFACE.Loading.show(false);
            throw new Error(renderingCapability.error || "Visualization rendering is unavailable on this device.");
        }

        // Expand any virtualized parent background (carrying a stored
        // `virtualization` decomposition) into first-class child backgrounds —
        // one per region — BEFORE wrapping, so children are wrapped uniformly.
        // Idempotent: reloaded sessions whose children already exist are skipped.
        BackgroundConfig.expandVirtualBackgrounds(appContext._dangerouslyAccessConfig());

        if (Array.isArray(config.background)) {
            config.background = config.background.map((bg: BackgroundItem | BackgroundConfig) => BackgroundConfig.from(bg, true, false));
        }

        // Fold any legacy shape carried in the freshly merged config:
        //   - `background[i].goalIndex` → `background[i].visualizationIndex`
        //   - top-level `activeVisualizationIndex` → distributed onto bg entries
        // BackgroundConfig.from already folded per-bg goalIndex on each entry
        // it wrapped; this call additionally handles the top-level param.
        BackgroundConfig.migrateLegacyConfig(appContext._dangerouslyAccessConfig());

        const cfg = appContext.config;
        const bgs: BackgroundConfig[] = Array.isArray(cfg.background) ? cfg.background : [];
        const vis = Array.isArray(cfg.visualizations) ? cfg.visualizations : [];
        const isSecureMode = !!appContext.secureMode;

        const selectionStateChanged = !!UTILITIES.parseBackgroundSelection(effectiveBgSpec);

        let activeBg = appContext.getOption("activeBackgroundIndex", undefined, true, true);

        // The canonical stored shape is an array; [] means "explicitly nothing
        // open" (parseBackgroundSelection maintains it). A scalar/undefined
        // here is a boot default or a legacy session import. With an empty
        // catalog the default 0 must not survive as an index.
        if (!Array.isArray(activeBg) && Number.isInteger(activeBg) && bgs.length === 0) {
            activeBg = undefined;
        }

        if (activeBg === undefined && bgs.length > 0) {
            // First boot / legacy import without a stored selection: default to
            // the first background.
            activeBg = 0;
        }

        if (typeof activeBg === "number") {
            activeBg = [activeBg];
            appContext.setOption("activeBackgroundIndex", activeBg);
        }

        // Defense in depth: a selection carried over from another context
        // (stale cache, hand-edited URL) may reference bg indices that don't
        // exist in THIS session. Clamp invalid entries and trim trailing
        // empty slots so they can't inflate the viewer count with phantom
        // empty viewers.
        if (Array.isArray(activeBg)) {
            const clamped = activeBg.map((i: any) =>
                Number.isInteger(i) && i >= 0 && i < bgs.length ? i : undefined);
            while (clamped.length > 1 && clamped[clamped.length - 1] === undefined) {
                clamped.pop();
            }
            // Only a stale NON-empty selection whose entries are all invalid
            // resets to [0]; an explicitly-empty selection ([]) is deliberate
            // ("nothing open") and must never resurrect background 0.
            if (bgs.length > 0 && activeBg.length > 0 && !clamped.some((i: any) => Number.isInteger(i))) {
                clamped.length = 0;
                clamped.push(0);
            }
            if (JSON.stringify(clamped) !== JSON.stringify(activeBg)) {
                activeBg = clamped;
                appContext.setOption("activeBackgroundIndex", activeBg);
            }
        }

        // Fold the `vizSpec` parameter (back-compat) into bg entries. Modern
        // callers should mutate `background[i].visualizationIndex` directly;
        // `vizSpec` remains accepted so existing scripting/session-restore
        // callers keep working.
        if (vizSpec !== undefined && Array.isArray(activeBg)) {
            const vizArr = vizSpec === null
                ? activeBg.map(() => null)
                : (Array.isArray(vizSpec) ? vizSpec : [vizSpec]);
            const broadcast = vizArr.length > 0 && Number.isInteger(vizArr[0])
                ? vizArr[0] as number
                : undefined;
            for (let slot = 0; slot < activeBg.length; slot++) {
                const bgIdx = activeBg[slot];
                if (!Number.isInteger(bgIdx) || !bgs[bgIdx as number]) continue;
                const value = slot < vizArr.length ? vizArr[slot] : broadcast;
                if (Number.isInteger(value)) {
                    (bgs[bgIdx as number] as any).visualizationIndex = value as number;
                } else if (value === null) {
                    (bgs[bgIdx as number] as any).visualizationIndex = null;
                }
            }
        }

        const nextSnapshot = captureLoadSnapshotFromConfig(config);
        // Override with the locally-normalized selection so change detection
        // and undo/redo see the same shape the pipeline works with (handles
        // scalar/absent legacy forms; canonical stored shape is an array,
        // [] = explicitly nothing open).
        (nextSnapshot as any).activeBackgroundIndex = normalizeHistorySelection(activeBg);
        const selectedBackgroundsBefore = selectedBackgroundIdsFromSnapshot(previousSnapshot);
        const selectedBackgroundsAfter = selectedBackgroundIdsFromSnapshot(nextSnapshot);
        const backgroundChanged = hadOpenViewerState && JSON.stringify(selectedBackgroundsBefore) !== JSON.stringify(selectedBackgroundsAfter);
        const explicitSelectionUpdate =
            bgSpec !== undefined ||
            vizSpec !== undefined;
        const selectionChangedDuringParse = hadOpenViewerState && explicitSelectionUpdate && selectionStateChanged;
        const anythingVisibleChanged = hadOpenViewerState && (
            visibleLoadChanged(previousSnapshot, nextSnapshot) ||
            selectionChangedDuringParse
        );
        const anythingChanged = hadOpenViewerState && (
            !loadSnapshotsEqual(previousSnapshot, nextSnapshot) ||
            selectionChangedDuringParse
        );

        const preserveHistoryOnBackgroundChange = opts.preserveHistoryOnBackgroundChange ?? parseLooseBoolean(
            appContext.config.params?.preserveHistoryOnBackgroundChange,
            false
        );
        const warnOnHistoryBoundary = opts.warnOnHistoryBoundary ?? parseLooseBoolean(
            appContext.config.params?.warnOnBackgroundHistoryBoundary,
            true
        );

        let historyMode = opts.historyMode || "auto";
        if (historyMode === "auto") {
            if (!anythingVisibleChanged) {
                historyMode = "skip";
            } else if (backgroundChanged) {
                historyMode = preserveHistoryOnBackgroundChange ? "content-switch" : "reset-history";
            } else {
                historyMode = "visualization-step";
            }
        }

        let maybeLoadingTimeout: any = undefined;
        if (backgroundChanged) {
            USER_INTERFACE.Loading.show(true);
        } else {
            maybeLoadingTimeout = setTimeout(() => {
                USER_INTERFACE.Loading.show(true);
                maybeLoadingTimeout = undefined;
            }, 1000);
        }

        // NOTE: do NOT gate the load on Dialogs.awaitHidden() here. Dialogs is
        // the (non-blocking) toast scheduler; awaiting it froze the whole load
        // behind a transient toast's full timeout while the opaque loading veil
        // covered that toast — undismissable. Error/warn toasts now render above
        // the loader (see toast.mjs z-index), so no serialization is needed.

        const hasCommittedHistory = !!history.hasAnyStackHistory();
        const closingToEmpty = selectedBackgroundsAfter.length === 0;
        if (!opts.fromHistory && backgroundChanged && historyMode === "reset-history" && hasCommittedHistory && !closingToEmpty) {
            const boundaryEvent = {
                previousSnapshot,
                nextSnapshot,
                historyMode,
                preserveHistoryOnBackgroundChange,
                cancel: false,
                preventDefault: false,
                message: "Changing the loaded background will clear undo history for the current content. Continue?",
            };

            await viewerManager.raiseEventAwaiting("before-history-boundary", boundaryEvent)
                .catch((e: any) => console.warn("Exception in 'before-history-boundary' event handler: ", e));

            if (boundaryEvent.cancel) {
                USER_INTERFACE.Loading.show(false);
                return false;
            }

            if (warnOnHistoryBoundary && !boundaryEvent.preventDefault && !window.confirm(boundaryEvent.message)) {
                USER_INTERFACE.Loading.show(false);
                return false;
            }
        }

        const bgPlan = (() => {
            // Explicitly-empty selection ([]) still needs ONE empty plan: the
            // single kept viewer must be walked so its content is cleared and
            // the "no data" placeholder page shows.
            if (Array.isArray(activeBg) && activeBg.length > 0) {
                return activeBg.map(idx => ({ type: "single", bgIndices: [idx] }));
            }
            if (Number.isInteger(activeBg)) {
                return [{ type: "single", bgIndices: [activeBg] }];
            }
            return [{ type: "single", bgIndices: [] }];
        })();

        const desiredCount = Math.max(1, bgPlan.length);
        const previousDesiredCount = Math.max(
            1,
            (normalizeHistorySelection(previousSnapshot?.activeBackgroundIndex) || []).length || 1
        );
        const changesViewerCount = desiredCount !== previousDesiredCount;
        const changesViewerNature = changesViewerCount || backgroundChanged;
        const refreshChangeKind = !anythingVisibleChanged ? "noop" : (changesViewerNature ? "content" : "visualization");

        await viewerManager.raiseEventAwaiting("before-refresh", {
            data: config.data,
            background: config.background,
            visualizations: config.visualizations,
            bgSpec: effectiveBgSpec,
            vizSpec,
            opts,
            historyMode,
            changeKind: refreshChangeKind,
            changesViewerNature,
            changesViewerCount,
            anythingVisibleChanged,
            anythingChanged,
            backgroundChanged,
        }).catch((e: any) => console.warn("Exception in 'before-refresh' event handler: ", e));

        for (let i = 0; i < desiredCount; i++) {
            if (!viewerManager.viewers[i]) viewerManager.add(i);
        }
        for (let i = viewerManager.viewers.length - 1; i >= desiredCount; i--) {
            if (i === 0) continue;
            viewerManager.delete(i);
        }

        /**
         * The HttpClient that protocol resolution built for a source we are about
         * to open, keyed by the source value it produced.
         *
         * The auth context belongs to the *protocol entry*, and a rendered URL
         * cannot carry that binding back: two entries on one upstream with
         * different `auth.contextId` render indistinguishable URLs, so recovering
         * the client by baseURL prefix would pick one at random (see
         * `SLIDE_PROTOCOLS.getActiveClientForUrl`, which now refuses that case).
         * Remembering it at resolve time is what keeps each slide on its own
         * credential.
         */
        const clientsBySource = new Map<any, any>();
        const clientForSource = (source: any) => clientsBySource.get(source);

        const bgUrlFromEntry = (bgEntry: BackgroundConfig, dataSpec: DataSpecification | undefined = undefined) => {
            const spec: DataSpecification | undefined = dataSpec === undefined ? BackgroundConfig.dataSpecification(bgEntry) : dataSpec;
            const resolved = (window as any).SLIDE_PROTOCOLS.resolveBackground({
                spec,
                bgEntry,
                isSecureMode,
            });
            const source = resolved.kind === "tileSource" ? resolved.tileSource : resolved.url;
            if (resolved.client) clientsBySource.set(source, resolved.client);
            return source;
        };

        // Renderer-side shader-config normalization wrappers were inlined here
        // and consumed by the bg/viz assembly walk. The walk now lives in
        // assemble-render-output.ts which holds its own thin delegates around
        // FlexRenderer.normalizeShaderConfig / normalizeShaderMap, so the
        // wrappers are no longer needed in this file.

        // A failed-open slot keeps a transparent placeholder whose `getConfig`
        // stays `undefined` (inert slot: no IO restore / annotation attach). But
        // consumers that enumerate open viewports — e.g. the slide switcher's
        // OPEN VIEWERS list — need to know *which* background this dead slot was
        // meant to hold, so it can be listed, flagged faulty, and closed. Stamp
        // the intended background config on a dedicated field, separate from
        // `getConfig`, so faulty detection (registry-driven) is untouched.
        const stampFaultyBackground = (item: any, kind: string | undefined, index: number, ctx: any) => {
            if (!item || kind !== "background") return;
            try {
                const bgIdx = typeof ctx?.bgIndexForItem === "function" ? ctx.bgIndexForItem(index) : undefined;
                const bgCfg = Number.isInteger(bgIdx) ? cfg.background[bgIdx as number] : undefined;
                if (bgCfg) item.__xopatFaultyBackground = bgCfg;
            } catch (e) {
                console.warn("[openPlaceholder] failed to stamp faulty background marker", e);
            }
        };

        const openPlaceholder = (viewer: OpenSeadragon.Viewer, errorMessage: any, index: number, originalSource: any, onOpen: (ok: boolean) => void, loadKey?: string, ctx?: any, kind?: string) => {
            // A real EmptyTileSource (rather than `{ type: "_blank" }`) so downstream
            // code that reads `item.source.dimensions` — annotations wrapper,
            // scalebar, navigator, etc. — sees a valid TiledImage instead of
            // crashing on an undefined `.source`. The dimensions mirror the
            // sentinel used by viewer-state-binding-controller.ts.
            const errorText =
                errorMessage ||
                $.t("error.slide.pending") + " " + $.t("error.slide.imageLoadFail") + " " +
                (originalSource && originalSource.toString ? originalSource.toString() : "");
            viewer.addTiledImage({
                tileSource: new (OpenSeadragon as any).EmptyTileSource({
                    width: 20000,
                    height: 20000,
                    tileSize: 512,
                    error: errorText,
                }),
                opacity: 0,
                index,
                success: (e: any) => {
                    e.item.__targetIndex = index;
                    e.item.getConfig = (_type: string | undefined) => undefined;
                    // Stamp the pipeline load key so the placeholder is a
                    // first-class surgical-reuse candidate (getExistingItemLoadKey
                    // reads __xopatLoadKey first). Without this an EmptyTileSource
                    // has no url/tileSourceId, its key degrades to `index:N`, and
                    // the next visualization switch fails to reuse it — re-opening
                    // (and silently un-marking) the faulty source. The key also
                    // lets the faulty-source registry resolve this item's verdict.
                    if (loadKey) e.item.__xopatLoadKey = loadKey;
                    stampFaultyBackground(e.item, kind, index, ctx);
                    // Give the placeholder a valid inert identity shader config
                    // (mirrors flex-renderer's own default). The flex drawer's
                    // rebuild maps `world._items -> item.__shaderConfig.id`; a
                    // configless world item makes it dereference `undefined.id`
                    // and cascades into renderer/WebGL crashes. An identity layer
                    // over an opacity-0 source renders nothing but keeps the
                    // renderer's "every item has a shader config" invariant.
                    if (!e.item.__shaderConfig) {
                        e.item.__shaderConfig = {
                            id: `__xopat_faulty_${index}`,
                            name: "Identity shader",
                            type: "identity",
                            visible: 1,
                            fixed: false,
                            params: {},
                            cache: {},
                        };
                    }
                    console.info(`[openPlaceholder] EmptyTileSource registered at index=${index}, worldCount=${viewer.world.getItemCount()}`);
                    onOpen(false);
                },
                error: (e: any) => {
                    console.error("[openPlaceholder] EmptyTileSource failed to attach:", e);
                    onOpen(false);
                }
            });
        };

        const deriveLoadKey = (dataSpec: any, source: any, fallbackIndex: number) => {
            try {
                const dataId = BackgroundConfig.dataFromSpec(dataSpec);
                if (dataId !== undefined) {
                    return `data:${typeof dataId === "string" ? dataId : safeStringify(dataId)}`;
                }
            } catch (_) {}

            const normalizedSource = source && typeof source === "object" && source.url !== undefined
                ? source.url
                : source;

            if (typeof normalizedSource === "string") return `source:${normalizedSource}`;

            try {
                const serialized = safeStringify(normalizedSource);
                if (serialized) return `source:${serialized}`;
            } catch (_) {}

            return `index:${fallbackIndex}`;
        };

        const configureOpenedItem = (item: any, kind: string, index: number, ctx: any) => {
            item.__targetIndex = index;
            if (ctx && typeof ctx.loadKeyForItem === "function") {
                item.__xopatLoadKey = ctx.loadKeyForItem(index);
            }

            if (kind === "background") {
                const bgIdx = ctx.bgIndexForItem(index);
                item.getConfig = (type: string | undefined) =>
                    !type || type === "background" ? cfg.background[bgIdx] : undefined;
            } else if (kind === "visualization") {
                const vIdx = ctx.vizIndexForItem(index);
                item.getConfig = (type: string | undefined) =>
                    !type || type === "visualization" ? cfg.visualizations[vIdx] : undefined;
            } else {
                item.getConfig = () => undefined;
            }

            const dataSpec = ctx && typeof ctx.dataForItem === "function"
                ? ctx.dataForItem(index)
                : undefined;

            // Same merge the protocol registry used pre-metadata, from the same
            // helper — a source that was constructed directly by SLIDE_PROTOCOLS
            // therefore sees an identical object twice (before its info request,
            // and here once the metadata is known so e.g. `channels: "all"` can
            // expand). See SlideProtocolRegistry.optionsFor.
            const sourceOptions = (window as any).SLIDE_PROTOCOLS.optionsFor(dataSpec, item.getConfig());

            if (sourceOptions !== undefined && item?.source?.setSourceOptions) {
                item.source.setSourceOptions(sourceOptions);
            }

            if (dataSpec && typeof dataSpec === "object"
                && typeof (dataSpec as any).imageSmoothingEnabled === "boolean") {
                const drawer = item?.viewer?.drawer;
                if (drawer && typeof drawer.setTiledImageSmoothingEnabled === "function") {
                    drawer.setTiledImageSmoothingEnabled(item, (dataSpec as any).imageSmoothingEnabled);
                }
            }

            applyPixelScale(item, index, ctx);
        };

        /**
         * Size an overlay by the pixel scale its data spec declares.
         *
         * OSD normalizes every world item to viewport width 1, so an overlay
         * lands on its background only when their aspect ratios match. An
         * overlay covering a whole number of blocks of a slide whose width is
         * NOT a whole number of blocks never matches: its edge block hangs past
         * the slide, OSD squeezes it back to fit, and every cell ends up
         * slightly small with the error accumulating across the image.
         *
         * `pixelScale` says how many reference pixels one of this image's
         * pixels covers, which is all that is needed to place it:
         *
         *     width = ownPixelWidth * pixelScale / referencePixelWidth
         *
         * Only the width is set — OSD derives height from the image's own
         * aspect ratio, which is already correct when both axes share a scale.
         *
         * Runs here rather than at `addTiledImage` time because neither pixel
         * width is known until the sources are ready. Tiles open sequentially
         * (see the `await openTile` loop), so a stack's reference is always in
         * the world before anything that scales against it.
         */
        const applyPixelScale = (item: any, index: number, ctx: any) => {
            const descriptor = ctx && typeof ctx.pixelScaleForItem === "function"
                ? ctx.pixelScaleForItem(index)
                : undefined;
            if (!descriptor) return;

            const reference = item?.viewer?.world?.getItemAt?.(descriptor.referenceIndex);
            // Scaling the reference against itself is meaningless, and a missing
            // one means there is nothing to be relative TO.
            if (!reference || reference === item) return;

            const width = computeOverlayWidth({
                ownWidth: item?.source?.dimensions?.x || item?.source?.width,
                referenceWidth: reference?.source?.dimensions?.x || reference?.source?.width,
                scaleX: descriptor.scaleX,
                placementWidth: descriptor.placementWidth,
            });
            if (width === undefined) return;

            try {
                item.setWidth(width, true);
            } catch (e) {
                console.warn("Failed to apply pixelScale to an opened item:", e);
            }
        };

        const getExistingItemLoadKey = (item: any, fallbackIndex: number) => {
            if (!item) return `missing:${fallbackIndex}`;
            if (item.__xopatLoadKey) return item.__xopatLoadKey;

            const configForItem = item.getConfig?.() || item.getConfig?.("background") || item.getConfig?.("visualization");
            const dataSpec = configForItem?.dataReference !== undefined
                ? cfg.data[configForItem.dataReference]
                : configForItem;
            return deriveLoadKey(dataSpec, item.source?.url || item.source, fallbackIndex);
        };

        const collectViewerDataIndexes = (backgroundIndex: number | undefined, visualizationIndex: number | undefined) => {
            const refs = new Set<number>();
            const bgEntry = Number.isInteger(backgroundIndex) ? cfg.background[backgroundIndex as number] : undefined;
            const vizEntry = Number.isInteger(visualizationIndex) ? cfg.visualizations[visualizationIndex as number] : undefined;

            if (bgEntry && typeof bgEntry.dataReference === "number") {
                refs.add(bgEntry.dataReference);
            }
            for (const shader of bgEntry?.shaders || []) {
                forEachNestedShaderLayer(shader, (entry) => {
                    for (const ref of entry?.dataReferences || []) {
                        if (Number.isInteger(ref)) refs.add(ref);
                    }
                });
            }
            if (vizEntry?.shaders) {
                forEachVisualizationShader(vizEntry.shaders as Record<string, any>, (shader) => {
                    for (const ref of shader?.dataReferences || []) {
                        if (Number.isInteger(ref)) refs.add(ref);
                    }
                });
            }
            return Array.from(refs.values());
        };

        const applyBeforeOpenMutations = async () => {
            for (let viewerIndex = 0; viewerIndex < bgPlan.length; viewerIndex += 1) {
                const entry = bgPlan[viewerIndex];
                const viewer = viewerManager.viewers[viewerIndex];
                let backgroundIndex = entry.bgIndices[0];
                const bgEntry: any = Number.isInteger(backgroundIndex) ? config.background[backgroundIndex as number] : undefined;
                let visualizationIndex = Number.isInteger(bgEntry?.visualizationIndex)
                    ? bgEntry.visualizationIndex as number
                    : undefined;
                let dataIndexes = collectViewerDataIndexes(backgroundIndex, visualizationIndex);

                const event = {
                    viewer,
                    viewerIndex,
                    entry,
                    backgroundIndex,
                    visualizationIndex,
                    background: Number.isInteger(backgroundIndex) ? config.background[backgroundIndex as number] : undefined,
                    visualization: Number.isInteger(visualizationIndex) ? config.visualizations[visualizationIndex as number] : undefined,
                    data: dataIndexes.map((index: number) => config.data[index]),
                    dataIndexes: [...dataIndexes],
                    opts,
                    changeKind: changesViewerCount || backgroundChanged ? "content" : "visualization",
                    changesViewerNature: changesViewerCount || backgroundChanged,
                    changesViewerCount,
                    isNewViewer: !viewer || !viewer.isOpen?.() || (viewer.world?.getItemCount?.() || 0) < 1,
                };

                await viewerManager.raiseEventAwaiting("before-open", event)
                    .catch((e: any) => console.warn("Exception in 'before-open' event handler: ", e));

                if (Number.isInteger(event.backgroundIndex)) {
                    backgroundIndex = event.backgroundIndex as number;
                    entry.bgIndices = [backgroundIndex];
                }
                if (Array.isArray(activeBg)) {
                    activeBg[viewerIndex] = backgroundIndex;
                } else if (Number.isInteger(backgroundIndex) && config.background[backgroundIndex as number]) {
                    activeBg = [backgroundIndex];
                }

                if (Number.isInteger(event.visualizationIndex)) {
                    visualizationIndex = event.visualizationIndex as number;
                } else if (event.visualizationIndex === null) {
                    // Explicit null from a handler means "no visualization for
                    // this slide" — without it a stale seeded index could not
                    // be cleared, only overridden.
                    visualizationIndex = undefined;
                }
                // Per-viewer viz state lives on the bg entry — write back any
                // override produced by the `before-open` handler chain.
                if (Number.isInteger(backgroundIndex) && config.background[backgroundIndex as number]) {
                    (config.background[backgroundIndex as number] as any).visualizationIndex =
                        Number.isInteger(visualizationIndex) ? visualizationIndex : null;
                }

                dataIndexes = Array.isArray(event.dataIndexes)
                    ? [...event.dataIndexes]
                    : collectViewerDataIndexes(backgroundIndex, visualizationIndex);

                if (Number.isInteger(backgroundIndex) && event.background && !Array.isArray(event.background)) {
                    config.background[backgroundIndex as number] = BackgroundConfig.from(event.background as BackgroundItem | BackgroundConfig, true, false);
                }
                if (Number.isInteger(visualizationIndex) && event.visualization) {
                    config.visualizations[visualizationIndex as number] = event.visualization;
                }
                if (Array.isArray(event.data)) {
                    dataIndexes.forEach((dataIndex: number, index: number) => {
                        if (index in event.data) {
                            config.data[dataIndex] = event.data[index];
                        }
                    });
                }
            }

            appContext.setOption("activeBackgroundIndex", activeBg);
        };

        const openTile = async (viewer: OpenSeadragon.Viewer, source: any, kind: string, index: number, ctx: any, placement?: any) => {
            const originalSource = source.source || source;
            const loadKey: string | undefined = typeof ctx?.loadKeyForItem === "function" ? ctx.loadKeyForItem(index) : undefined;
            const faultyRegistry: any = (viewer as any).__faultySources;
            // Determine the per-protocol HttpClient (if any). Preferred source of
            // truth is the client protocol resolution already built for THIS item
            // (`ctx.clientForItem`) — a URL cannot identify its own auth context, so
            // two protocol entries on one upstream with different credentials are
            // indistinguishable by baseURL prefix. A pre-built TileSource carries
            // `__xopatHttpClient` from resolve time; the prefix lookup is the last
            // resort for sources that never went through the registry. The active
            // client is set during instantiation so OSD's metadata fetch (via the
            // patched makeAjaxRequest) routes through it; afterwards we stamp the
            // resulting source so the patched downloadTileStart picks it up for
            // every tile.
            const SP = (window as any).SLIDE_PROTOCOLS;
            const client = (typeof ctx?.clientForItem === "function" ? ctx.clientForItem(index) : undefined)
                ?? (typeof originalSource === "string"
                    ? SP?.getActiveClientForUrl?.(originalSource)
                    : originalSource?.__xopatHttpClient);
            // A source the registry already constructed (explicit `tileSourceClass`
            // or a factory protocol) started fetching its metadata at construction
            // time. Route it through `awaitSourceReady` instead of OSD's
            // `instantiateTileSourceClass`: the latter's already-a-TileSource branch
            // does nothing but wait, and it cannot see an `open-failed` that fired
            // before it subscribed — which would hang the open forever.
            const isPrebuilt = !!originalSource && typeof originalSource === "object"
                && originalSource instanceof (window as any).OpenSeadragon.TileSource;
            const tileSource = isPrebuilt
                ? await SP.awaitSourceReady(originalSource).catch((e: any) => e?.message || String(e))
                : await SP.withActiveClient(client, () =>
                    viewer.instantiateTileSourceClass({ tileSource: originalSource })
                        .then((ev: any) => ev.source)
                        .catch((ev: any) => ev.message || String(ev))
                );
            if (client && tileSource && typeof tileSource === "object" && !tileSource.error && !(tileSource as any).__xopatHttpClient) {
                (tileSource as any).__xopatHttpClient = client;
            }

            if (typeof tileSource === "string" || (typeof tileSource === "object" && tileSource.error) || tileSource instanceof Error) {
                console.error(`Failed to instantiate tile source for ${kind} ${index}: ${tileSource}`);
                const errorText = typeof tileSource === "object" && tileSource.error
                    ? String(tileSource.error)
                    : String(tileSource);
                // Persist the verdict so it survives rebuilds / viz switches.
                faultyRegistry?.markFaulty?.(loadKey, errorText, "instantiation");
                await viewer.raiseEventAwaiting(
                    "tile-source-failed", { viewer, originalSource, kind, index, tileSource: null, error: tileSource }
                ).catch((e: any) => console.warn("Exception in 'tile-source-failed' event handler: ", e));
                return new Promise<boolean>(resolve => openPlaceholder(viewer, tileSource, index, originalSource, resolve, loadKey, ctx, kind));
            }

            await viewer.raiseEventAwaiting(
                "tile-source-created",
                { viewer, originalSource, kind, index, tileSource, error: null }
            ).catch((e: any) => console.warn("Exception in 'tile-source-created' event handler: ", e));
            console.log("Opening tile", kind, index, ctx);

            // Eligibility is the SOURCE's call, not the layer's role: a data
            // overlay is a tiled image like any other and pays the same
            // first-paint cost, so it benefits identically. A source for which
            // a preview would be wrong refuses on its own — via
            // `__noPreviewLevel`, via a non-8-bit `getTilePrecision()` (the
            // synthetic tile is an 8-bit raster), or simply by not implementing
            // `getThumbnail()`, which is why vector sources fall out for free.
            try { (tileSource as any).tryInjectPreviewLevel?.(); } catch (e) {
                console.warn("Preview-level injection failed:", e);
            }

            // Per-cut viewport placement (OVERLAID mode): position + SAME pixel
            // scale (width = region fraction). OSD has no `flipped` ctor option, so
            // it is applied post-add via setFlip. `undefined` placement → OSD default.
            //
            // A source may also ask for a placement of its own — geometry it read
            // out of the file rather than anything the session chose. The canonical
            // cases are DICOM `ImageOrientationSlide`, which says how the slide sits
            // on the glass, and a derived object whose raster covers only part of
            // the matrix it declares.
            //
            // The two COMPOSE rather than override: the session says which region of
            // the viewport this image's frame occupies, the file says where the image
            // sits inside that frame, and `degrees` add. A source must never ask for a
            // flip — OSD honours a flip when drawing and not when converting
            // coordinates, which would leave annotations unmirrored on mirrored pixels.
            const intrinsic: any = (() => {
                try { return (tileSource as any).getIntrinsicPlacement?.() || null; } catch (e) {
                    console.warn("Intrinsic placement failed:", e);
                    return null;
                }
            })();
            const degrees = (Number(placement?.degrees) || 0) + (Number(intrinsic?.degrees) || 0);
            const iX = Number(intrinsic?.x) || 0;
            const iY = Number(intrinsic?.y) || 0;
            const iWidth = Number.isFinite(Number(intrinsic?.width)) ? Number(intrinsic.width) : 1;
            // `(0, 0, 1)` is "the whole frame" — the default, and what a source that
            // reports only a rotation means. Anything else has to reach OSD even when
            // the session chose no placement at all.
            const hasIntrinsicRect = iX !== 0 || iY !== 0 || iWidth !== 1;
            let placementOpts: any;
            if (placement) {
                const pWidth = Number(placement.width) || 1;
                placementOpts = {
                    x: (Number(placement.x) || 0) + pWidth * iX,
                    y: (Number(placement.y) || 0) + pWidth * iY,
                    width: pWidth * iWidth,
                    degrees,
                };
            } else if (hasIntrinsicRect) {
                placementOpts = { x: iX, y: iY, width: iWidth, degrees };
            } else {
                placementOpts = degrees ? { degrees } : {};
            }
            return new Promise<boolean>((resolve) => {
                viewer.addTiledImage({
                    tileSource,
                    index,
                    ...placementOpts,
                    success: (event: any) => {
                        configureOpenedItem(event.item, kind, index, ctx);
                        if (placement?.flipped) { try { event.item.setFlip?.(true); } catch (_) {} }
                        resolve(true);
                    },
                    error: (e: any) => {
                        console.warn(e);
                        faultyRegistry?.markFaulty?.(loadKey, String(e?.message || e), "instantiation");
                        openPlaceholder(viewer, e.message || e, index, originalSource, resolve, loadKey, ctx, kind);
                    }
                });
            });
        };

        /**
         * Apply the viewer's canvas clear color: the opened background's `fill`
         * override, else the session/deployment `setup.backgroundColor`
         * (`BackgroundConfig.resolveFillColor`).
         *
         * Applied per VIEWER, not per opened tile: a surgical rebuild reuses world
         * items without going through `openTile`, and the clear color is a property
         * of the drawer, not of one tiled image. Always applied (never conditionally
         * skipped) so switching from a `fill`-carrying slide back to a plain one
         * restores the global default instead of keeping the previous slide's color.
         *
         * MUST run before the renderer rebuilds its programs: `setBackground` only
         * takes effect on the next shader compile (flex-renderer `setBackground`),
         * which `overrideConfigureAll` below performs.
         *
         * `drawerOptions` is restamped too — `OpenSeadragon.makeStandaloneFlexDrawer`
         * clones them, so offscreen drawers (thumbnails, region exports, vision
         * inference images) render on the same background as the screen. Existing
         * offscreen drawers are updated in place for the same reason.
         */
        const applyViewerFillColor = (viewer: any, bg: any) => {
            const color = BackgroundConfig.resolveFillColor(bg);
            try {
                viewer.drawer?.renderer?.setBackground?.(color);
                viewer.navigator?.drawer?.renderer?.setBackground?.(color);
                const drawerOptions = viewer.drawerOptions?.["flex-renderer"];
                if (drawerOptions) drawerOptions.backgroundColor = color;
                viewer.__ofscreenRender?.renderer?.setBackground?.(color);
                viewer.__scriptVisualizationStandaloneDrawer?.renderer?.setBackground?.(color);
            } catch (e) {
                console.warn("Failed to apply background fill color.", e);
            }
        };

        const beginViewerRenderTransaction = (viewer: OpenSeadragon.Viewer) => {
            const drawer: any = viewer?.drawer;
            const navigatorDrawer: any = viewer?.navigator?.drawer;

            try {
                drawer?.suspendRendering?.("xopat-open");
            } catch (e) {
                console.warn("Flex drawer suspendRendering failed.", e);
            }
            try {
                navigatorDrawer?.suspendRendering?.("xopat-open");
            } catch (e) {
                console.warn("Navigator flex drawer suspendRendering failed.", e);
            }

            return {
                finish: () => {
                    try {
                        navigatorDrawer?.resumeRendering?.("xopat-open");
                    } catch (e) {
                        console.warn("Navigator flex drawer resumeRendering failed.", e);
                    }
                    try {
                        drawer?.resumeRendering?.("xopat-open");
                    } catch (e) {
                        console.warn("Flex drawer resumeRendering failed.", e);
                    }
                }
            };
        };

        await applyBeforeOpenMutations();

        const effectiveSnapshot = captureLoadSnapshotFromConfig(config);
        // Override with the locally-normalized selection so downstream
        // consumers (per-viewer changeKind, state-binding controller, session
        // sync) see the same normalized array shape the pipeline works with.
        // Viz selection lives on bg entries already cloned into the snapshot.
        (effectiveSnapshot as any).activeBackgroundIndex = normalizeHistorySelection(activeBg);
        const viewerUpdatePlans = bgPlan.map((entry: any, viewerIndex: number) => {
            const viewer = viewerManager.viewers[viewerIndex];
            const previousNatureFingerprint = buildViewerNatureFingerprint(previousSnapshot, viewerIndex);
            const nextNatureFingerprint = buildViewerNatureFingerprint(effectiveSnapshot, viewerIndex);
            const previousRenderFingerprint = buildViewerRenderFingerprint(previousSnapshot, viewerIndex);
            const nextRenderFingerprint = buildViewerRenderFingerprint(effectiveSnapshot, viewerIndex);
            const previousBgSelection = selectionAt(previousSnapshot?.activeBackgroundIndex, viewerIndex);
            const nextBgSelection = selectionAt(effectiveSnapshot?.activeBackgroundIndex, viewerIndex);
            const previousVizSelection = vizIndexFromSnapshot(previousSnapshot, viewerIndex);
            const nextVizSelection = vizIndexFromSnapshot(effectiveSnapshot, viewerIndex);
            const selectionChangedForViewer =
                previousBgSelection !== nextBgSelection ||
                previousVizSelection !== nextVizSelection;
            const visualizationSelectionChangedForViewer =
                previousVizSelection !== nextVizSelection;
            const isNewViewer = !viewer || !viewer.isOpen?.() || (viewer.world?.getItemCount?.() || 0) < 1;

            let changeKind: "noop" | "content" | "visualization";
            if (isNewViewer || opts.force) {
                changeKind = "content";
            } else if (selectionChangedForViewer) {
                changeKind = previousBgSelection !== nextBgSelection || changesViewerCount
                    ? "content"
                    : "visualization";
            } else if (previousRenderFingerprint === nextRenderFingerprint) {
                changeKind = "noop";
            } else if (previousNatureFingerprint !== nextNatureFingerprint || changesViewerCount) {
                changeKind = "content";
            } else {
                changeKind = "visualization";
            }

            if (appContext.getOption("webglDebugMode")) {
                console.log(`[pipeline] plan v=${viewerIndex}`, {
                    changeKind,
                    previousBgSelection, nextBgSelection,
                    previousVizSelection, nextVizSelection,
                    prevBgEntryViz: Number.isInteger(previousBgSelection)
                        ? (previousSnapshot?.background?.[previousBgSelection as number] as any)?.visualizationIndex
                        : "(no bg)",
                    nextBgEntryViz: Number.isInteger(nextBgSelection)
                        ? (effectiveSnapshot?.background?.[nextBgSelection as number] as any)?.visualizationIndex
                        : "(no bg)",
                    liveBgEntryViz: Number.isInteger(nextBgSelection)
                        ? (bgs[nextBgSelection as number] as any)?.visualizationIndex
                        : "(no bg)",
                    prevActiveBg: previousSnapshot?.activeBackgroundIndex,
                    nextActiveBg: effectiveSnapshot?.activeBackgroundIndex,
                });
            }

            return {
                entry,
                viewerIndex,
                changeKind,
                visualizationSelectionChangedForViewer,
            };
        });

        const debugOn = !!appContext.getOption("webglDebugMode");
        const plog = (phase: string, data?: any) => {
            if (!debugOn) return;
            if (data !== undefined) console.log(`[pipeline] ${phase}`, data);
            else console.log(`[pipeline] ${phase}`);
        };

        const openIntoViewer = async (plan: any) => {
            const { entry, viewerIndex } = plan;
            const viewer = viewerManager.viewers[viewerIndex];
            plog(`openIntoViewer ENTER v=${viewerIndex}`, {
                changeKind: plan.changeKind,
                isOpen: viewer.isOpen?.(),
                worldCount: viewer.world?.getItemCount?.(),
            });
            if (plan.changeKind === "noop" && viewer.isOpen() && viewer.world.getItemCount() > 0) {
                plog(`openIntoViewer SKIP v=${viewerIndex} (noop)`);
                return { skipped: true, viewerIndex };
            }

            const canSurgicallyDiff = plan.changeKind !== "content" && viewer.isOpen() && viewer.world.getItemCount() > 0;
            const viewerSupportsFlexRendering = !!(
                viewer?.drawer &&
                typeof viewer.drawer.getType === "function" &&
                viewer.drawer.getType() === "flex-renderer" &&
                typeof viewer.drawer.overrideConfigureAll === "function"
            );

            const openedBase: BackgroundConfig[] = [];
            const bgi = entry.bgIndices[0];
            if (Number.isInteger(bgi) && bgs[bgi]) openedBase.push(bgs[bgi]);

            // Viz for this viewer is the slot's bg entry's `visualizationIndex`.
            let visIndexForThis: number | undefined;
            const bgForViewer: any = Number.isInteger(bgi) ? bgs[bgi] : undefined;
            if (bgForViewer && Number.isInteger(bgForViewer.visualizationIndex)) {
                visIndexForThis = bgForViewer.visualizationIndex as number;
            }

            const renderingWithWebGL = viewerSupportsFlexRendering && renderingCapability.ok && Array.isArray(vis) && vis.length > 0 && Number.isInteger(visIndexForThis);
            const activeV = renderingWithWebGL ? vis[visIndexForThis as number] : undefined;
            if (!renderingWithWebGL && Array.isArray(vis) && vis.length > 0 && Number.isInteger(visIndexForThis) && (!viewerSupportsFlexRendering || !renderingCapability.ok)) {
                visualizationRuntime.warnRenderingCapability(renderingCapability.error || "Visualization rendering is unavailable; opening image data without visualization shaders.");
            }
            // An index that points past the end of the collection. `activeV` is
            // then `undefined`, `assembleVisualizationShaders` returns on the
            // spot, and the viewer opens background-only — previously with no
            // signal at all, which is indistinguishable from a session that
            // deliberately asked for no overlays. That case is NOT this one:
            // "no visualization" is `visualizationIndex` absent or explicitly
            // `null`, and neither reaches here (`Number.isInteger` guards both).
            if (renderingWithWebGL && !activeV) {
                visualizationRuntime.warnVisualization($.t("error.visualizationIndexMissing", { index: visIndexForThis }));
            }

            const toOpen: any[] = [];
            // Reset per stack (in assembleStack) so two regions referencing the
            // SAME data index (e.g. a shared heatmap) each get their OWN cropped
            // world item instead of colliding on the first region's crop.
            // `worldIndexEntries` accumulates across stacks for the playground's
            // data→world map. buildManagedShaderSourceEntry closes over the `let`
            // and so sees the current stack's map.
            let uniqueOsdWorldIndexes: Map<any, number> = new Map();
            const worldIndexEntries: [any, number][] = [];
            const openedSpecOrder: any[] = [];
            const renderOutput: Record<string, any> = {};
            // Per-tile kind ("background" | "visualization"), parallel to `toOpen`.
            // Replaces a single `firstVizIndex` boundary so that OVERLAID mode can
            // interleave N per-region stacks (each bg + viz) in one viewer.
            const tileKinds: string[] = [];
            // Per-tile viewport placement (`{x,y,width,degrees,flipped}` | undefined),
            // parallel to `toOpen`. OVERLAID cuts are placed at width = region.w so
            // every cut renders at the SAME pixel scale (not OSD's default fit-to-1).
            // `undefined` = OSD default (non-overlaid viewers fit their own content).
            // `stackPlacement` is the current stack's placement; the shared
            // buildManagedShaderSourceEntry reads it for time-series tiles.
            const tilePlacements: (any | undefined)[] = [];
            // Per-tile HttpClient from protocol resolution (`clientForSource`),
            // parallel to `toOpen`. `undefined` = this source did not come from the
            // registry (or its protocol declares no transport) and `openTile` falls
            // back to the baseURL-prefix lookup.
            const tileClients: (any | undefined)[] = [];
            // Per-tile `pixelScale` placement (`{scaleX, referenceIndex, placementWidth}`
            // | undefined), parallel to `toOpen`. Unlike `tilePlacements` this is
            // PER ENTRY, not per stack: it says how big one pixel of THIS image is
            // relative to its stack's background, which is the whole point — a
            // stack-wide value could not express "the overlay is coarser than the
            // slide". Consumed by `applyPixelScale` once both sources are ready.
            const tileScales: (any | undefined)[] = [];
            let stackPlacement: any | undefined = undefined;
            // World index of the current stack's background — the image every
            // `pixelScale` in this stack is relative to.
            let stackBaseWorldIndex: number | undefined = undefined;

            // Per-region crop propagation + the bg/viz source factories live inside
            // `assembleStack` below (parameterized by each stack's croppingContext),
            // so one viewer can render N region stacks (overlaid) — not just one.

            const isSeriesLikeMeta = (meta: any = {}) =>
                meta?.param === "series" || meta?.shaderType === "time-series";

            const shouldForceManagedShaderSourceEntry = (meta: any = {}) =>
                meta?.forceManaged === true || isSeriesLikeMeta(meta);

            const computeActiveSeriesIndex = (shaderConfig: any): number => {
                const timeline = shaderConfig?.params?.timeline || {};
                const min = Number(timeline.min);
                const step = Number(timeline.step);
                const def = Number(timeline.default);
                if (!Number.isFinite(def)) return 0;
                const base = Number.isFinite(min) ? min : 0;
                const stride = Number.isFinite(step) && step > 0 ? step : 1;
                return Math.max(0, Math.round((def - base) / stride));
            };

            const shaderSourceController: ViewerShaderSourceController | undefined =
                (viewer as any).__shaderSourceController;

            const buildManagedShaderSourceEntry = (
                dataIndex: number,
                sourceFactory: (dataIndex: number) => any,
                meta: any = {}
            ) => {
                // Re-normalization passthrough: entry was already resolved to
                // an integer world index or an opaque token on a prior pass.
                if (!Number.isInteger(dataIndex)) {
                    return dataIndex;
                }

                if ((dataIndex as number) < 0 || (dataIndex as number) >= cfg.data.length) {
                    return dataIndex;
                }

                const existingWorldIndex = uniqueOsdWorldIndexes.get(dataIndex);

                // Non-series / non-forced: cheap integer rebind if already opened,
                // else just return the raw data index for the caller to handle.
                if (!shouldForceManagedShaderSourceEntry(meta)) {
                    if (Number.isInteger(existingWorldIndex)) return existingWorldIndex;
                    return dataIndex;
                }

                // Series entries need runtime-reroutable bindings. We pre-open
                // only the active frame and hand out opaque tokens for the rest
                // so runtime scrubs go through our resolver (no library append).
                if (isSeriesLikeMeta(meta) && shaderSourceController) {
                    const tileSource = sourceFactory(dataIndex as number);
                    const loadKey = deriveLoadKey(cfg.data[dataIndex as number], tileSource, dataIndex as number);
                    // opacity > 0 is required so the flex-renderer's first-pass
                    // actually paints this tile into its atlas layer
                    // (flex-renderer.js:_drawTwoPassFirst gates on getOpacity() > 0).
                    // OSD's classic compositor is bypassed by the two-pass model,
                    // so this opacity does NOT affect on-screen layering — it only
                    // controls whether the source is sampleable by the second pass.
                    shaderSourceController.registerDataSource(loadKey, () => ({
                        tileSource: sourceFactory(dataIndex as number),
                        // todo: test 0
                        openOptions: { opacity: 1 },
                    }));

                    const activeIndex = computeActiveSeriesIndex(meta?.config);
                    const isActiveEntry = meta?.entryIndex === activeIndex;

                    if (isActiveEntry) {
                        let worldIndex = existingWorldIndex;
                        if (!Number.isInteger(worldIndex)) {
                            worldIndex = toOpen.length;
                            uniqueOsdWorldIndexes.set(dataIndex, worldIndex);
                            toOpen.push(tileSource);
                            openedSpecOrder.push(cfg.data[dataIndex as number]);
                            tileKinds.push("visualization");
                            tilePlacements.push(stackPlacement);
                            tileClients.push(clientForSource(tileSource));
                            worldIndexEntries.push([dataIndex, worldIndex]);
                        }
                        const shaderId = meta?.config?.id || "shader";
                        shaderSourceController.registerShaderBinding(worldIndex as number, shaderId, 0, loadKey);
                        return worldIndex as number;
                    }

                    return makeXOpatSourceToken(loadKey, {
                        dataIndex: dataIndex as number,
                        shaderType: meta?.shaderType,
                        param: meta?.param,
                        entryIndex: meta?.entryIndex,
                    });
                }

                // Legacy forceManaged path: preserve the library descriptor shape.
                return {
                    worldIndex: Number.isInteger(existingWorldIndex) ? existingWorldIndex : undefined,
                    tileSource: sourceFactory(dataIndex as number),
                    openOptions: {
                        opacity: 0,
                    },
                };
            };

            // Visualization shaders dropped during assembly because their data
            // could not be resolved (e.g. no "visualization" slide protocol in
            // this deployment + a stale session's shaders). Collected across
            // stacks; surfaced once per open below.
            const skippedVizShaders: string[] = [];

            // Assemble ONE region "stack" (a background + its visualization, cropped
            // via `croppingContext`) into the shared `toOpen` / `renderOutput`.
            // NONE/SIDEBYSIDE run this once (the selected bg). OVERLAID runs it once
            // per child region — N full cropped stacks in THIS one viewer, each
            // shader-id sub-namespaced by the child id so they don't collide.
            const assembleStack = (
                stackBg: BackgroundConfig,
                croppingContext: VirtualCroppingContext | undefined,
                stackVizIndex: number | undefined,
                prefix: string | null,
                placement?: any,
            ): void => {
                if (!stackBg) return;
                // Fresh allocation map per stack (see declaration). The shared
                // `toOpen` keeps growing; only the data→world dedup is per stack.
                uniqueOsdWorldIndexes = new Map();
                // Every tile this stack opens gets the stack's viewport placement
                // (bg + viz are co-registered). Read by buildManagedShaderSourceEntry.
                stackPlacement = placement;
                stackBaseWorldIndex = undefined;
                const stackActiveV = (renderingWithWebGL && Number.isInteger(stackVizIndex)
                    && Array.isArray(vis) && vis[stackVizIndex as number])
                    ? vis[stackVizIndex as number] : undefined;

                // Crop a data spec to this stack's region. The background image AND
                // every visualization data layer route through the `virtual-region`
                // protocol with the child's crop, so the whole stack crops together.
                const cropSpec = (spec: DataSpecification): DataSpecification => {
                    if (!croppingContext) {
                        // console.warn("[vr-trace] cropSpec SKIP no-croppingContext-in-scope", { prefix, specProtocol: (spec as any)?.protocol, specKeys: spec && typeof spec === "object" ? Object.keys(spec) : typeof spec });
                        return spec;
                    }
                    if (spec && typeof spec === "object" && (spec as DataOverride).croppingContext) {
                        // console.warn("[vr-trace] cropSpec PASSTHROUGH already-has-context", { prefix, specProtocol: (spec as any)?.protocol });
                        return spec;
                    }
                    const baseId = BackgroundConfig.dataFromSpec(spec);
                    if (baseId === undefined) {
                        // console.warn("[vr-trace] cropSpec SKIP no-baseId", { prefix, specKeys: spec && typeof spec === "object" ? Object.keys(spec) : typeof spec });
                        return spec;
                    }
                    const wrapped: DataOverride = { dataID: baseId, protocol: "virtual-region", croppingContext };
                    if (spec && typeof spec === "object") {
                        const o = spec as DataOverride;
                        if (o.microns != null) wrapped.microns = o.microns;
                        if (o.micronsX != null) wrapped.micronsX = o.micronsX;
                        if (o.micronsY != null) wrapped.micronsY = o.micronsY;
                        if (o.options) wrapped.options = o.options;
                    }
                    return wrapped;
                };
                const vizUrl = (dataIndex: number) => {
                    const spec = cropSpec(cfg.data[dataIndex] as DataSpecification);
                    const resolved = (window as any).SLIDE_PROTOCOLS.resolveVisualization({ spec, vizEntry: stackActiveV, isSecureMode });
                    const source = resolved.kind === "tileSource" ? resolved.tileSource : resolved.url;
                    if (resolved.client) clientsBySource.set(source, resolved.client);
                    return source;
                };
                const allocate = (dataIndex: number, kind: "background" | "visualization", ref?: BackgroundConfig): number => {
                    if (uniqueOsdWorldIndexes.has(dataIndex)) return uniqueOsdWorldIndexes.get(dataIndex) as number;
                    const allocated = toOpen.length;
                    uniqueOsdWorldIndexes.set(dataIndex, allocated);
                    const source = kind === "background" && ref
                        ? bgUrlFromEntry(ref, cropSpec(cfg.data[dataIndex] as DataSpecification))
                        : vizUrl(dataIndex);
                    toOpen.push(source);
                    openedSpecOrder.push(cfg.data[dataIndex]);
                    tileKinds.push(kind);
                    tilePlacements.push(placement);
                    // Scale is relative to THIS stack's background, so it is only
                    // meaningful once that background has a world slot — which it
                    // always does here, allocated just below before any call.
                    const scaleX = readPixelScale(cfg.data[dataIndex],
                        (message, value) => console.warn(`[open-pipeline] data[${dataIndex}]: ${message}`, value));
                    tileScales.push(scaleX !== undefined && stackBaseWorldIndex !== undefined
                        ? { scaleX, referenceIndex: stackBaseWorldIndex, placementWidth: placement?.width }
                        : undefined);
                    tileClients.push(clientForSource(source));
                    worldIndexEntries.push([dataIndex, allocated]);
                    return allocated;
                };

                // The stack's primary background image. It IS the reference, so it
                // never carries a scale of its own.
                const baseIndex = stackBg.dataReference;
                if (!uniqueOsdWorldIndexes.has(baseIndex)) {
                    const allocated = toOpen.length;
                    uniqueOsdWorldIndexes.set(baseIndex, allocated);
                    stackBaseWorldIndex = allocated;
                    const baseSource = bgUrlFromEntry(stackBg);
                    toOpen.push(baseSource);
                    openedSpecOrder.push(BackgroundConfig.dataSpecification(stackBg));
                    tileKinds.push("background");
                    tilePlacements.push(placement);
                    tileScales.push(undefined);
                    tileClients.push(clientForSource(baseSource));
                    worldIndexEntries.push([baseIndex, allocated]);
                }

                const env = {
                    backgrounds: [stackBg],
                    activeVisualization: renderingWithWebGL ? stackActiveV : undefined,
                    data: cfg.data,
                    cloneRuntimeState,
                    resolveWorldIndex: allocate,
                    expandDataSourceRef: (entry: any, kind: "background" | "visualization", ref: BackgroundConfig | undefined, meta: any) => buildManagedShaderSourceEntry(
                        entry,
                        kind === "background" && ref
                            ? (dataIndex: number) => bgUrlFromEntry(ref, cropSpec(cfg.data[dataIndex] as DataSpecification))
                            : vizUrl,
                        meta,
                    ),
                    onShaderSkipped: (shaderId: string, _error: unknown) => skippedVizShaders.push(shaderId),
                };

                const stackRO: Record<string, any> = {};
                assembleBackgroundShaders(env, stackRO);
                assembleVisualizationShaders(env, stackRO);

                if (prefix) {
                    Object.assign(renderOutput, renameShaderIds(stackRO, buildShaderIdNamespace(prefix, "r")));
                } else {
                    Object.assign(renderOutput, stackRO);
                }
            };

            if (renderingWithWebGL && activeV) {
                appContext.prepareRendering();
            }

            // OVERLAID: the selected bg is a parent whose child regions render
            // co-resident in this one viewer (identity stays the parent — see ctx).
            // Otherwise: a single stack (the selected bg, with its own crop if it is
            // itself a side-by-side child).
            const overlaidChildren: BackgroundConfig[] =
                (bgForViewer && (bgForViewer as any).virtualizationMode === "overlaid" && (bgForViewer as any).virtualization)
                    ? (bgs as any[]).filter((b: any) => b && b.virtualOf === (bgForViewer as any).id) as BackgroundConfig[]
                    : [];
            if (overlaidChildren.length > 0) {
                // Place each cut at width = region.w so every cut shares the parent's
                // pixel scale (instead of OSD fitting each to width=1). Position from
                // the region's transform (identity → stacked at the common origin);
                // a future registration step just writes dx/dy/rotation/flip.
                const placementFor = (cc: VirtualCroppingContext | undefined): any => {
                    if (!cc || !cc.region) return undefined;
                    const r = cc.region;
                    const t: any = cc.transform || {};
                    return {
                        x: Number(t.dx) || 0,
                        y: Number(t.dy) || 0,
                        width: r.w,
                        degrees: Number(t.rotation) || 0,
                        flipped: !!t.flip,
                    };
                };
                for (const child of overlaidChildren) {
                    const cc = (child as any).croppingContext;
                    assembleStack(child, cc, visIndexForThis, (child as any).id, placementFor(cc));
                }
            } else {
                assembleStack(bgForViewer, (bgForViewer as any)?.croppingContext, visIndexForThis, null);
            }

            if (skippedVizShaders.length) {
                console.warn(`[viewer-open] ${skippedVizShaders.length} visualization shader(s) skipped (unresolvable data):`, skippedVizShaders);
                Dialogs.show($.t("error.visualizationShaderSkipped"), 8000, Dialogs.MSG_WARN);
            }

            // Cross-shader binding refs: the resolver's "sole user vs shared"
            // decision relies on knowing every shader that references a world
            // index, not just the ones we pre-opened. Walk the assembled
            // renderOutput once so bg+viz overlap (which is common) doesn't
            // make the resolver wrongly swap in place.
            if (shaderSourceController) {
                shaderSourceController.resetBindings();
                const registerRefsFor = (config: any, idOverride?: string) => {
                    if (!config || typeof config !== "object") return;
                    const shaderId = idOverride || config.id || "shader";
                    const tiledImages = Array.isArray(config.tiledImages) ? config.tiledImages : [];
                    for (let i = 0; i < tiledImages.length; i++) {
                        const w = tiledImages[i];
                        if (Number.isInteger(w) && (w as number) >= 0) {
                            shaderSourceController.registerShaderBinding(w as number, shaderId, i);
                        }
                    }
                    if (Array.isArray(config.series)) {
                        // time-series active entry is an integer; non-active are tokens.
                        for (const entry of config.series) {
                            if (Number.isInteger(entry) && (entry as number) >= 0) {
                                shaderSourceController.registerShaderBinding(entry as number, shaderId, 0);
                            }
                        }
                    }
                    if (config.shaders && typeof config.shaders === "object" && !Array.isArray(config.shaders)) {
                        for (const [childId, child] of Object.entries(config.shaders)) {
                            registerRefsFor(child, `${shaderId}/${childId}`);
                        }
                    }
                };
                for (const [rootId, rootCfg] of Object.entries(renderOutput)) {
                    registerRefsFor(rootCfg, (rootCfg as any)?.id || rootId);
                }
            }

            const loadKeys = toOpen.map((source, index) => deriveLoadKey(openedSpecOrder[index], source, index));

            // Slide-aware IO lifecycle (per-viewer-background bundle scope):
            // flush the just-vacated (viewer, background) before the world is
            // cleared so owners (annotations, …) can snapshot their state
            // keyed by the OLD slide. Only fires for content-kind changes
            // (visualization-only / noop don't change the background id),
            // and only when a previous bg id exists (skips fresh viewers).
            // Per-viewer-only and global bundleScopes ignore this dispatch
            // — opting OUT keeps state loaded across slide swaps.
            const viewerUniqueIdBeforeReset = viewer?.uniqueId;
            const previousBackgroundId = (window as any).UTILITIES?.currentBackgroundIdFor?.(viewer);
            if (
                plan.changeKind === "content" &&
                viewerUniqueIdBeforeReset &&
                previousBackgroundId
            ) {
                try {
                    await (window as any).IO_PIPELINE?.flushBundleExport?.({
                        viewerId: viewerUniqueIdBeforeReset,
                        backgroundId: previousBackgroundId,
                        // Same reasoning as the restore below: this snapshot of
                        // the slide being left is the pipeline's own upkeep,
                        // triggered by navigation rather than requested. A
                        // refusal belongs in the log, not in the user's way.
                        trigger: "system",
                    });
                } catch (e) {
                    console.warn("IO flush for vacated slide failed:", e);
                }
            }

            // Suspend rendering BEFORE _resetViewer so any flex rebuilds
            // scheduled by remove-item events fire under _isRenderingSuspended()
            // and defer cleanly instead of running against a half-torn-down world.
            const renderTransaction = beginViewerRenderTransaction(viewer);
            plog(`openIntoViewer TRANSACTION BEGAN v=${viewerIndex}`);

            let successOpened = 0;
            const retainedItems = new Set<any>();
            try {
                if (!canSurgicallyDiff) {
                    viewerManager._resetViewer(viewerIndex);
                    // A full reset is a genuine content change (new slide /
                    // viewer nature). Drop persisted faulty verdicts so a
                    // re-opened source is attempted afresh; a viz-only surgical
                    // rebuild keeps them, which is what makes the warning
                    // survive a visualization switch.
                    (viewer as any).__faultySources?.clear?.();
                }

                const ctx = {
                    bgIndexForItem: (i: number) => entry.bgIndices[0],
                    vizIndexForItem: (i: number) => visIndexForThis,
                    dataForItem: (i: number) => openedSpecOrder[i],
                    loadKeyForItem: (i: number) => loadKeys[i],
                    clientForItem: (i: number) => tileClients[i],
                    pixelScaleForItem: (i: number) => tileScales[i],
                };

                plog(`openIntoViewer PLAN v=${viewerIndex}`, {
                    toOpen: toOpen.length,
                    tileKinds,
                    renderOutputIds: Object.keys(renderOutput),
                    canSurgicallyDiff,
                    viewerSupportsFlexRendering,
                });

                plog(`openIntoViewer TILE LOOP START v=${viewerIndex}`);
                const faultyRegistry: any = (viewer as any).__faultySources;
                for (let i = 0; i < toOpen.length; i++) {
                    const kind = tileKinds[i] || "background";

                    // A source that failed to instantiate can never render.
                    // Re-instantiating it on every rebuild hammers a dead
                    // endpoint and momentarily un-marks it (the new item has a
                    // defined getConfig and no error metadata), which is the
                    // exact mechanism by which a faulty background lost its
                    // warning on a visualization switch. Keep a transparent
                    // placeholder at this slot instead; the registry retains
                    // the verdict so the navigator + shader menu stay marked.
                    if (faultyRegistry?.isInstantiationFaulty?.(loadKeys[i])) {
                        let placeholder = canSurgicallyDiff ? viewer.world.getItemAt(i) : null;
                        if (!(placeholder && getExistingItemLoadKey(placeholder, i) === loadKeys[i] && !retainedItems.has(placeholder))) {
                            await new Promise<boolean>(resolve =>
                                openPlaceholder(viewer, faultyRegistry.getError(loadKeys[i]), i, toOpen[i], resolve, loadKeys[i], ctx, kind));
                            placeholder = viewer.world.getItemAt(i);
                        }
                        // Deliberately NOT configureOpenedItem'd: the placeholder
                        // keeps getConfig === undefined, preserving the legacy
                        // navigator faulty signal alongside the registry verdict.
                        // Still (re-)stamp the intended background marker so a
                        // reused placeholder from a pre-marker rebuild is listable.
                        if (placeholder) {
                            stampFaultyBackground(placeholder, kind, i, ctx);
                            retainedItems.add(placeholder);
                        }
                        continue;
                    }

                    let reusable = canSurgicallyDiff ? viewer.world.getItemAt(i) : null;

                    if (reusable && getExistingItemLoadKey(reusable, i) === loadKeys[i] && !retainedItems.has(reusable)) {
                        configureOpenedItem(reusable, kind, i, ctx);
                        retainedItems.add(reusable);
                        successOpened++;
                        continue;
                    }

                    reusable = null;
                    if (canSurgicallyDiff) {
                        const currentCount = viewer.world.getItemCount();
                        for (let j = i + 1; j < currentCount; j++) {
                            const candidate = viewer.world.getItemAt(j);
                            if (!candidate || retainedItems.has(candidate)) continue;
                            if (getExistingItemLoadKey(candidate, j) !== loadKeys[i]) continue;
                            reusable = candidate;
                            break;
                        }
                    }

                    if (reusable) {
                        viewer.world.setItemIndex(reusable, i);
                        configureOpenedItem(reusable, kind, i, ctx);
                        retainedItems.add(reusable);
                        successOpened++;
                        continue;
                    }

                    if (await openTile(viewer, toOpen[i], kind, i, ctx, tilePlacements[i])) {
                        const openedItem = viewer.world.getItemAt(i);
                        if (openedItem) retainedItems.add(openedItem);
                        successOpened++;
                    }
                }

                if (canSurgicallyDiff) {
                    for (let i = viewer.world.getItemCount() - 1; i >= 0; i--) {
                        const item = viewer.world.getItemAt(i);
                        if (!retainedItems.has(item)) {
                            viewer.world.removeItem(item);
                        }
                    }
                }

                // Stash the dataIndex → worldIndex map on the viewer so
                // sandboxed clones (the Visualization Playground) can build
                // their renderer config against the same world layout this
                // pipeline just established. Plain object so it survives a
                // structured clone path if anyone serialises it later.
                (viewer as any).__dataToWorldIndex = worldIndexEntries;

                plog(`openIntoViewer TILE LOOP DONE v=${viewerIndex}`, {
                    successOpened,
                    worldCount: viewer.world.getItemCount(),
                });

                const applyRendererConfiguration = async () => {
                    if (!viewerSupportsFlexRendering || !viewer.drawer?.overrideConfigureAll) {
                        return false;
                    }

                    // If there are no shaders to apply, transition the drawer
                    // back to internally-managed mode. This is the only path
                    // that genuinely needs `overrideConfigureAll(undefined)`.
                    if (!Object.keys(renderOutput).length) {
                        await viewer.drawer.overrideConfigureAll(undefined);
                        return false;
                    }

                    // `overrideConfigureAll(renderOutput)` does its own
                    // `deleteShaders()` + recreate (flex-renderer.js ~10128),
                    // so a preceding `overrideConfigureAll(undefined)` to
                    // "reset on visualization change" just builds per-item
                    // internal configs that are immediately discarded — and
                    // trips a library bug in the external→internal transition
                    // (the navigator drawer's `tiledImageCreated` dereferences
                    // a parent tiledImage's already-deleted `__shaderConfig`).
                    // Drop the redundant pre-reset; the apply call handles
                    // the swap.

                    // Step 1: apply cached shader state to the un-prefixed
                    // renderOutput. `applyStoredVisualizationSnapshot` keys
                    // its named/path lookups by structural shader id, so
                    // the per-viewer prefix MUST NOT be visible here.
                    UTILITIES.applyStoredVisualizationSnapshot(renderOutput);

                    // Step 2: per-viewer shader-id namespace. FlexRenderer
                    // builds HTML control DOM ids as `${shader.id}_${ctrl}`
                    // and looks them up via `document.getElementById` —
                    // global. Two viewers showing the same shader id
                    // collide on `noUiSlider.create` ("Slider was already
                    // initialized"). Prefix the renderer's shader ids per
                    // viewer; structural config and session payloads stay
                    // un-prefixed via the strip path in
                    // `UTILITIES.exportLiveVisualization`.
                    const shaderNamespace = buildShaderIdNamespace(viewer.id ?? viewerIndex, "v");
                    const namespacedRenderOutput = renameShaderIds(renderOutput, shaderNamespace);
                    (viewer as any).__shaderNamespace = shaderNamespace;

                    const attemptApply = async () => {
                        await viewer.drawer.overrideConfigureAll(namespacedRenderOutput);
                        return true;
                    };

                    try {
                        return await attemptApply();
                    } catch (error) {
                        console.warn("Renderer configuration failed, retrying without cached shader state.", error);
                        if (!visualizationRuntime.clearVisualizationCaches(namespacedRenderOutput)) {
                            throw error;
                        }
                        return await attemptApply();
                    }
                };

                // Clear color, before any (re)build below bakes it into the compile.
                applyViewerFillColor(viewer, bgForViewer);

                // A viewer whose every source failed to instantiate holds only
                // an inert placeholder and shows the demo overlay below. Feeding
                // its (faulty) render output into the shared flex renderer
                // corrupts the shared program and races the grid-cell teardown,
                // cascading into renderer/WebGL crashes that also break the
                // sibling (valid) viewports. Skip configuration entirely for a
                // fully-failed viewer.
                if (viewerSupportsFlexRendering && successOpened > 0) {
                    try {
                        plog(`openIntoViewer waitForViewerRenderReady BEGIN v=${viewerIndex}`);
                        await this.waitForViewerRenderReady(viewer);
                        plog(`openIntoViewer waitForViewerRenderReady DONE v=${viewerIndex}`);
                        plog(`openIntoViewer waitForNavigatorParity BEGIN v=${viewerIndex}`, {
                            mainCount: viewer.world?.getItemCount?.(),
                            navCount: (viewer.navigator as any)?.world?.getItemCount?.(),
                        });
                        await this.waitForNavigatorParity(viewer);
                        plog(`openIntoViewer waitForNavigatorParity DONE v=${viewerIndex}`, {
                            mainCount: viewer.world?.getItemCount?.(),
                            navCount: (viewer.navigator as any)?.world?.getItemCount?.(),
                        });
                        plog(`openIntoViewer applyRendererConfiguration BEGIN v=${viewerIndex}`);
                        await applyRendererConfiguration();
                        plog(`openIntoViewer applyRendererConfiguration DONE v=${viewerIndex}`);
                    } catch (error) {
                        try {
                            await viewer.drawer.overrideConfigureAll(undefined);
                        } catch (resetError) {
                            console.warn("Failed to reset renderer after configuration failure.", resetError);
                        }

                        if (strictVisualization) {
                            throw error;
                        }

                        console.error("Visualization renderer configuration failed.", error);
                        if (!opts.suppressDialogsOnVisualizationFailure) {
                            Dialogs.show($.t("error.slide.failed"), 15000, Dialogs.MSG_WARN);
                        }
                    }
                } else if (viewerSupportsFlexRendering) {
                    // Fully-failed viewer (only an inert placeholder). Put the
                    // drawer in a clean EMPTY external-config state rather than
                    // feeding it the faulty background's render output or leaving
                    // it in internal-config mode. External mode keeps
                    // `_configuredExternally` true (so the crash-prone
                    // `world._items.map(i => i.__shaderConfig.id)` rebuild branch
                    // is skipped) and, with no shaders, emits no `htmlHandler`
                    // layer card — so the faulty viewport shows only the demo
                    // overlay, not a spurious "Identity shader" entry.
                    try {
                        await viewer.drawer.overrideConfigureAll({});
                    } catch (e) {
                        console.warn("Failed to clear renderer for fully-failed viewer.", e);
                    }
                }

                if (!canSurgicallyDiff || viewer.world.getItemCount() < 1) {
                    stateBindings.handleSyntheticOpenEvent(viewer);
                } else {
                    stateBindings.refreshViewerVisualizationBindings(viewer, 0);
                }

                if (successOpened === 0) {
                    viewer.toggleDemoPage(true, toOpen.length > 0 ? $.t("error.invalidDataHtml") : undefined);
                } else {
                    viewer.toggleDemoPage(false);
                }

                // Slide-aware IO lifecycle (per-viewer-background bundle scope):
                // restore the (viewer, new background) snapshot now that the
                // world holds the new content. Mirrors the pre-reset flush
                // above; same opt-in semantics (no-op for owners that didn't
                // declare per-viewer-background / all). Re-resolves the bg
                // id from the live world so it reflects the post-open state.
                if (plan.changeKind === "content") {
                    const nextBackgroundId = (window as any).UTILITIES?.currentBackgroundIdFor?.(viewer);
                    const nextViewerUniqueId = viewer?.uniqueId;
                    if (nextViewerUniqueId && nextBackgroundId) {
                        try {
                            await (window as any).IO_PIPELINE?.tryRestoreImport?.({
                                viewerId: nextViewerUniqueId,
                                backgroundId: nextBackgroundId,
                                // The user opened a slide, not a restore. What
                                // hydrates behind that is the pipeline's
                                // bookkeeping, so a refusal is logged rather
                                // than turned into a dialog about an action
                                // nobody performed.
                                trigger: "system",
                            });
                        } catch (e) {
                            console.warn("IO restore for new slide failed:", e);
                        }
                    }
                }
            } finally {
                plog(`openIntoViewer TRANSACTION FINISH v=${viewerIndex}`);
                renderTransaction.finish();
                // After resume, drive an explicit paint so the canvas reflects
                // the post-open state — particularly the close-to-empty case
                // where flex's pending rebuild would otherwise leave the
                // previous slide's tiles in the GPU texture cache until some
                // other event (mouse move, zoom) nudges OSD to repaint.
                try { viewer.forceRedraw?.(); } catch (_) {}
                plog(`openIntoViewer EXIT v=${viewerIndex}`);
            }
        };

        // DEFINITIVE async-dims fix: eagerly load each virtual-region PARENT's
        // geometry and seed the cropped-source cache BEFORE the open loop builds
        // any cropped source. A cropped source that finds its parent geometry in
        // the cache is tall from construction, so OSD never builds a TiledImage
        // from the square placeholder (which permanently caches a square
        // contentAspectX / first-pass texture). Only runs when virtual-region
        // sources are present.
        await (async () => {
            const OSDref: any = (window as any).OpenSeadragon;
            const seed = OSDref?.CroppedTileSource?.seedParentGeometry;
            const SP: any = (window as any).SLIDE_PROTOCOLS;
            const probeViewer: any = viewerManager?.viewers?.[0];
            const dataArr: any[] = Array.isArray(config.data) ? config.data : [];
            const hasVirtual = dataArr.some((s: any) => s && typeof s === "object" && s.protocol === "virtual-region");
            if (!seed || !SP || !probeViewer || !hasVirtual) return;
            const seen = new Set<string>();
            const jobs: Promise<void>[] = [];
            for (const spec of dataArr) {
                if (!spec) continue;
                if (typeof spec === "object" && spec.protocol === "virtual-region") continue; // skip overrides
                const dataId = (typeof spec === "object" && (spec as any).dataID) ? (spec as any).dataID : spec;
                const pid = typeof dataId === "string" ? dataId : JSON.stringify(dataId);
                if (seen.has(pid)) continue;
                seen.add(pid);
                jobs.push((async () => {
                    try {
                        const resolved = SP.resolveBackground({ spec: dataId, isSecureMode });
                        const srcInput = resolved.kind === "tileSource" ? resolved.tileSource : resolved.url;
                        // Resolution knows which entry (and therefore which credential)
                        // owns this source; a URL does not.
                        const client = resolved.client
                            ?? (resolved.kind === "tileSource" ? resolved.tileSource?.__xopatHttpClient : undefined);
                        const ev = await SP.withActiveClient(client, () =>
                            probeViewer.instantiateTileSourceClass({ tileSource: srcInput }));
                        if (ev?.source) seed(pid, ev.source);
                    } catch (e) {
                        console.warn("[virtualization] parent geometry preload failed:", pid, e);
                    }
                })());
            }
            await Promise.all(jobs);
        })();

        const loadTooLongTimeout = setTimeout(
            () => Dialogs.show($.t("error.slide.pending"), 15000, Dialogs.MSG_WARN),
            8000
        );

        let openSucceeded = true;
        await Promise.allSettled(viewerUpdatePlans.map(openIntoViewer)).then(async e => {
            let hadRejectedOpen = false;
            for (const promise of e) {
                if (promise.status === "rejected") {
                    hadRejectedOpen = true;
                    console.error("Failed to open viewer item", promise.reason);
                    Dialogs.show($.t("error.slide.failed"), 15000, Dialogs.MSG_WARN);
                }
            }

            if (hadRejectedOpen && strictVisualization) {
                throw new Error("Failed to apply one or more visualization updates.");
            }

            if (maybeLoadingTimeout) {
                clearTimeout(maybeLoadingTimeout);
                maybeLoadingTimeout = undefined;
            }
            clearTimeout(loadTooLongTimeout);

            // Bind active viewer BEFORE plugins boot, so window.VIEWER and
            // module.viewer getters resolve to a real viewer in pluginReady().
            if (!viewerManager.get() && viewerManager.viewers.length > 0) {
                viewerManager.setActive(0, "open-complete");
            }

            if (backgroundChanged || anythingVisibleChanged) {
                viewerManager.raiseEvent("after-open");
            }

            // Plugins' pluginReady() runs only after the world is populated
            // and the active viewer is bound — see plan/lifecycle ordering fix.
            await this.deps.runLoaderOnce();

            USER_INTERFACE.Loading.show(false);
            appContext.setDirty();
            UTILITIES.updateTheme(null);
            UTILITIES.syncOpenedViewersToSession();
            UTILITIES.syncSessionToUrl(false);

            if (!opts.fromHistory && history && history.isRecordingEnabled !== false && anythingChanged) {
                if (historyMode === "reset-history") {
                    const resetSnapshot = captureLoadSnapshotFromConfig(config);
                    // Reflect the locally-normalized selection (see nextSnapshot
                    // override above).
                    (resetSnapshot as any).activeBackgroundIndex = normalizeHistorySelection(activeBg);
                    history.clear?.({
                        kind: "load-history-reset",
                        reason: "background-changed",
                        previousSnapshot,
                        nextSnapshot: resetSnapshot,
                    });
                } else if (historyMode === "content-switch" || historyMode === "visualization-step") {
                    const appliedSnapshot = captureLoadSnapshotFromConfig(config);
                    (appliedSnapshot as any).activeBackgroundIndex = normalizeHistorySelection(activeBg);
                    history.pushExecuted?.(
                        () => restoreLoadSnapshot(appliedSnapshot),
                        () => restoreLoadSnapshot(previousSnapshot),
                        {
                            kind: historyMode,
                            label: opts.historyLabel || (historyMode === "content-switch" ? "content switch" : "visualization change"),
                        }
                    );
                }
            }
        }).catch(e => {
            openSucceeded = false;
            if (maybeLoadingTimeout) {
                clearTimeout(maybeLoadingTimeout);
            }
            clearTimeout(loadTooLongTimeout);
            console.error("Failed to open viewer items", e);
            USER_INTERFACE.Loading.show(false);
            Dialogs.show($.t("error.slide.failed"), 15000, Dialogs.MSG_ERROR);
            if (strictVisualization) {
                throw e;
            }
        });
        return openSucceeded;
    }
}
